/**
 * Documentation for built-in functions, constants, and shell commands.
 * Used by the `man` command to display inline help.
 */

export interface FunctionDoc {
  name: string;
  category: string;
  synopsis: string;
  description: string;
  parameters?: Array<{
    name: string;
    type: string;
    description: string;
    optional?: boolean;
    default?: string;
  }>;
  returns?: string;
  examples?: Array<{
    code: string;
    result?: string;
    description?: string;
  }>;
  seeAlso?: string[];
}

export interface ConstantDoc {
  name: string;
  value: string;
  description: string;
}

export interface CommandDoc {
  name: string;
  synopsis: string;
  description: string;
  options?: Array<{
    flag: string;
    description: string;
  }>;
  examples?: Array<{
    code: string;
    description?: string;
  }>;
}

// Shell commands documentation
export const shellCommands: Record<string, CommandDoc> = {
  man: {
    name: "man",
    synopsis: "man <topic>",
    description:
      "Display manual page for a built-in function, constant, or shell command. " +
      "Use 'man -k <keyword>' to search for topics containing the keyword.",
    options: [
      {
        flag: "-k <keyword>",
        description: "Search for topics matching keyword",
      },
      { flag: "-l", description: "List all available topics" },
    ],
    examples: [
      { code: "man sin", description: "Show help for the sin() function" },
      {
        code: "man -k matrix",
        description: "Search for matrix-related topics",
      },
      { code: "man -l", description: "List all available manual pages" },
    ],
  },
  clear: {
    name: "clear",
    synopsis: "clear",
    description: "Clear the terminal output history for the current session.",
    examples: [{ code: "clear", description: "Clear all output" }],
  },
  help: {
    name: "help",
    synopsis: "help [topic]",
    description:
      "Display general help or help on a specific topic. " +
      "Alias for 'man' when a topic is provided.",
    examples: [
      { code: "help", description: "Show general help" },
      { code: "help sin", description: "Same as 'man sin'" },
    ],
  },
  env: {
    name: "env",
    synopsis: "env",
    description:
      "Display all variables and functions defined in the current environment.",
    examples: [{ code: "env", description: "Show current environment" }],
  },
  reset: {
    name: "reset",
    synopsis: "reset",
    description:
      "Reset the current session's environment, clearing all defined variables and functions.",
    examples: [
      { code: "reset", description: "Clear all variables and start fresh" },
    ],
  },
  history: {
    name: "history",
    synopsis: "history [n]",
    description:
      "Display command history. Optionally specify a number to show only the last n commands.",
    examples: [
      { code: "history", description: "Show all command history" },
      { code: "history 10", description: "Show last 10 commands" },
    ],
  },
};

// Built-in constants documentation
export const constants: Record<string, ConstantDoc> = {
  pi: {
    name: "pi",
    value: "3.141592653589793",
    description:
      "The mathematical constant π (pi), the ratio of a circle's circumference to its diameter.",
  },
  e: {
    name: "e",
    value: "2.718281828459045",
    description: "Euler's number, the base of the natural logarithm.",
  },
  tau: {
    name: "tau",
    value: "6.283185307179586",
    description:
      "The mathematical constant τ (tau), equal to 2π. Represents a full turn in radians.",
  },
  phi: {
    name: "phi",
    value: "1.618033988749895",
    description: "The golden ratio φ (phi), equal to (1 + √5) / 2.",
  },
  inf: {
    name: "inf",
    value: "Infinity",
    description: "Positive infinity. Use -inf for negative infinity.",
  },
  nan: {
    name: "nan",
    value: "NaN",
    description: "Not a Number. Result of undefined operations like 0/0.",
  },
  i: {
    name: "i",
    value: "√-1",
    description:
      "The imaginary unit, defined as the square root of -1. Used for complex numbers.",
  },
  true: {
    name: "true",
    value: "1",
    description: "Boolean true value.",
  },
  false: {
    name: "false",
    value: "0",
    description: "Boolean false value.",
  },
};

// Built-in functions documentation
export const functions: Record<string, FunctionDoc> = {
  // Basic Math
  abs: {
    name: "abs",
    category: "Math",
    synopsis: "abs(x)",
    description:
      "Returns the absolute value of x. For complex numbers, returns the magnitude.",
    parameters: [
      {
        name: "x",
        type: "number | complex | array",
        description: "Input value",
      },
    ],
    returns: "Absolute value or magnitude",
    examples: [
      { code: "abs(-5)", result: "5" },
      {
        code: "abs(3 + 4i)",
        result: "5",
        description: "Magnitude of complex number",
      },
      {
        code: "abs([-1, 2, -3])",
        result: "[1, 2, 3]",
        description: "Element-wise",
      },
    ],
    seeAlso: ["sign", "sqrt"],
  },
  sign: {
    name: "sign",
    category: "Math",
    synopsis: "sign(x)",
    description:
      "Returns the sign of x: -1 for negative, 0 for zero, 1 for positive.",
    parameters: [
      { name: "x", type: "number | array", description: "Input value" },
    ],
    returns: "-1, 0, or 1",
    examples: [
      { code: "sign(-5)", result: "-1" },
      { code: "sign(0)", result: "0" },
      { code: "sign(42)", result: "1" },
    ],
    seeAlso: ["abs"],
  },
  sqrt: {
    name: "sqrt",
    category: "Math",
    synopsis: "sqrt(x)",
    description:
      "Returns the square root of x. For negative numbers, returns a complex result.",
    parameters: [
      {
        name: "x",
        type: "number | complex | array",
        description: "Input value",
      },
    ],
    returns: "Square root of x",
    examples: [
      { code: "sqrt(16)", result: "4" },
      { code: "sqrt(-1)", result: "i", description: "Returns complex number" },
      { code: "sqrt([1, 4, 9])", result: "[1, 2, 3]" },
    ],
    seeAlso: ["cbrt", "pow"],
  },
  cbrt: {
    name: "cbrt",
    category: "Math",
    synopsis: "cbrt(x)",
    description: "Returns the cube root of x.",
    parameters: [
      { name: "x", type: "number | array", description: "Input value" },
    ],
    returns: "Cube root of x",
    examples: [
      { code: "cbrt(8)", result: "2" },
      { code: "cbrt(-27)", result: "-3" },
    ],
    seeAlso: ["sqrt", "pow"],
  },
  pow: {
    name: "pow",
    category: "Math",
    synopsis: "pow(base, exponent)",
    description:
      "Returns base raised to the power of exponent. Equivalent to base^exponent.",
    parameters: [
      { name: "base", type: "number | complex", description: "Base value" },
      {
        name: "exponent",
        type: "number | complex",
        description: "Exponent value",
      },
    ],
    returns: "base^exponent",
    examples: [
      { code: "pow(2, 10)", result: "1024" },
      { code: "2^10", result: "1024", description: "Operator form" },
      { code: "pow(e, i*pi)", result: "-1", description: "Euler's identity" },
    ],
    seeAlso: ["sqrt", "exp", "ln"],
  },
  exp: {
    name: "exp",
    category: "Math",
    synopsis: "exp(x)",
    description: "Returns e raised to the power of x.",
    parameters: [
      {
        name: "x",
        type: "number | complex | array",
        description: "Exponent value",
      },
    ],
    returns: "e^x",
    examples: [
      { code: "exp(1)", result: "2.718...", description: "Returns e" },
      { code: "exp(0)", result: "1" },
      { code: "exp(i*pi)", result: "-1", description: "Euler's identity" },
    ],
    seeAlso: ["ln", "pow"],
  },
  ln: {
    name: "ln",
    category: "Math",
    synopsis: "ln(x)",
    description: "Returns the natural logarithm (base e) of x.",
    parameters: [
      {
        name: "x",
        type: "number | complex | array",
        description: "Input value",
      },
    ],
    returns: "Natural log of x",
    examples: [
      { code: "ln(e)", result: "1" },
      { code: "ln(1)", result: "0" },
      { code: "ln(-1)", result: "πi", description: "Complex result" },
    ],
    seeAlso: ["log", "log10", "exp"],
  },
  log: {
    name: "log",
    category: "Math",
    synopsis: "log(x, [base])",
    description:
      "Returns the logarithm of x. If base is omitted, returns natural logarithm.",
    parameters: [
      {
        name: "x",
        type: "number | complex | array",
        description: "Input value",
      },
      {
        name: "base",
        type: "number",
        description: "Logarithm base",
        optional: true,
        default: "e",
      },
    ],
    returns: "Logarithm of x",
    examples: [
      { code: "log(100, 10)", result: "2" },
      { code: "log(8, 2)", result: "3" },
      { code: "log(e)", result: "1", description: "Natural log when no base" },
    ],
    seeAlso: ["ln", "log2", "log10"],
  },
  log2: {
    name: "log2",
    category: "Math",
    synopsis: "log2(x)",
    description: "Returns the base-2 logarithm of x.",
    parameters: [
      { name: "x", type: "number | array", description: "Input value" },
    ],
    returns: "Base-2 logarithm of x",
    examples: [
      { code: "log2(8)", result: "3" },
      { code: "log2(1024)", result: "10" },
    ],
    seeAlso: ["log", "log10"],
  },
  log10: {
    name: "log10",
    category: "Math",
    synopsis: "log10(x)",
    description: "Returns the base-10 logarithm of x.",
    parameters: [
      { name: "x", type: "number | array", description: "Input value" },
    ],
    returns: "Base-10 logarithm of x",
    examples: [
      { code: "log10(100)", result: "2" },
      { code: "log10(1000)", result: "3" },
    ],
    seeAlso: ["log", "log2"],
  },

  // Trigonometric
  sin: {
    name: "sin",
    category: "Trigonometry",
    synopsis: "sin(x)",
    description: "Returns the sine of x (x in radians).",
    parameters: [
      {
        name: "x",
        type: "number | complex | array",
        description: "Angle in radians",
      },
    ],
    returns: "Sine of x",
    examples: [
      { code: "sin(0)", result: "0" },
      { code: "sin(pi/2)", result: "1" },
      { code: "sin(pi/6)", result: "0.5" },
    ],
    seeAlso: ["cos", "tan", "asin"],
  },
  cos: {
    name: "cos",
    category: "Trigonometry",
    synopsis: "cos(x)",
    description: "Returns the cosine of x (x in radians).",
    parameters: [
      {
        name: "x",
        type: "number | complex | array",
        description: "Angle in radians",
      },
    ],
    returns: "Cosine of x",
    examples: [
      { code: "cos(0)", result: "1" },
      { code: "cos(pi)", result: "-1" },
      { code: "cos(pi/3)", result: "0.5" },
    ],
    seeAlso: ["sin", "tan", "acos"],
  },
  tan: {
    name: "tan",
    category: "Trigonometry",
    synopsis: "tan(x)",
    description: "Returns the tangent of x (x in radians).",
    parameters: [
      {
        name: "x",
        type: "number | complex | array",
        description: "Angle in radians",
      },
    ],
    returns: "Tangent of x",
    examples: [
      { code: "tan(0)", result: "0" },
      { code: "tan(pi/4)", result: "1" },
    ],
    seeAlso: ["sin", "cos", "atan"],
  },
  asin: {
    name: "asin",
    category: "Trigonometry",
    synopsis: "asin(x)",
    description: "Returns the arcsine of x (result in radians).",
    parameters: [
      {
        name: "x",
        type: "number | array",
        description: "Value between -1 and 1",
      },
    ],
    returns: "Arcsine of x in radians",
    examples: [
      { code: "asin(0)", result: "0" },
      { code: "asin(1)", result: "π/2" },
      { code: "asin(0.5)", result: "π/6" },
    ],
    seeAlso: ["sin", "acos", "atan"],
  },
  acos: {
    name: "acos",
    category: "Trigonometry",
    synopsis: "acos(x)",
    description: "Returns the arccosine of x (result in radians).",
    parameters: [
      {
        name: "x",
        type: "number | array",
        description: "Value between -1 and 1",
      },
    ],
    returns: "Arccosine of x in radians",
    examples: [
      { code: "acos(1)", result: "0" },
      { code: "acos(0)", result: "π/2" },
      { code: "acos(-1)", result: "π" },
    ],
    seeAlso: ["cos", "asin", "atan"],
  },
  atan: {
    name: "atan",
    category: "Trigonometry",
    synopsis: "atan(x)",
    description: "Returns the arctangent of x (result in radians).",
    parameters: [
      { name: "x", type: "number | array", description: "Input value" },
    ],
    returns: "Arctangent of x in radians",
    examples: [
      { code: "atan(0)", result: "0" },
      { code: "atan(1)", result: "π/4" },
      { code: "atan(inf)", result: "π/2" },
    ],
    seeAlso: ["tan", "atan2", "asin", "acos"],
  },
  atan2: {
    name: "atan2",
    category: "Trigonometry",
    synopsis: "atan2(y, x)",
    description:
      "Returns the angle in radians between the positive x-axis and the point (x, y).",
    parameters: [
      { name: "y", type: "number", description: "Y coordinate" },
      { name: "x", type: "number", description: "X coordinate" },
    ],
    returns: "Angle in radians",
    examples: [
      { code: "atan2(1, 1)", result: "π/4" },
      { code: "atan2(1, 0)", result: "π/2" },
      { code: "atan2(-1, -1)", result: "-3π/4" },
    ],
    seeAlso: ["atan"],
  },

  // Hyperbolic
  sinh: {
    name: "sinh",
    category: "Hyperbolic",
    synopsis: "sinh(x)",
    description: "Returns the hyperbolic sine of x.",
    parameters: [
      {
        name: "x",
        type: "number | complex | array",
        description: "Input value",
      },
    ],
    returns: "Hyperbolic sine of x",
    examples: [
      { code: "sinh(0)", result: "0" },
      { code: "sinh(1)", result: "1.1752..." },
    ],
    seeAlso: ["cosh", "tanh", "asinh"],
  },
  cosh: {
    name: "cosh",
    category: "Hyperbolic",
    synopsis: "cosh(x)",
    description: "Returns the hyperbolic cosine of x.",
    parameters: [
      {
        name: "x",
        type: "number | complex | array",
        description: "Input value",
      },
    ],
    returns: "Hyperbolic cosine of x",
    examples: [
      { code: "cosh(0)", result: "1" },
      { code: "cosh(1)", result: "1.5430..." },
    ],
    seeAlso: ["sinh", "tanh", "acosh"],
  },
  tanh: {
    name: "tanh",
    category: "Hyperbolic",
    synopsis: "tanh(x)",
    description: "Returns the hyperbolic tangent of x.",
    parameters: [
      {
        name: "x",
        type: "number | complex | array",
        description: "Input value",
      },
    ],
    returns: "Hyperbolic tangent of x",
    examples: [
      { code: "tanh(0)", result: "0" },
      { code: "tanh(inf)", result: "1" },
    ],
    seeAlso: ["sinh", "cosh", "atanh"],
  },

  // Rounding
  floor: {
    name: "floor",
    category: "Rounding",
    synopsis: "floor(x)",
    description: "Returns the largest integer less than or equal to x.",
    parameters: [
      { name: "x", type: "number | array", description: "Input value" },
    ],
    returns: "Floored value",
    examples: [
      { code: "floor(3.7)", result: "3" },
      { code: "floor(-3.2)", result: "-4" },
    ],
    seeAlso: ["ceil", "round", "trunc"],
  },
  ceil: {
    name: "ceil",
    category: "Rounding",
    synopsis: "ceil(x)",
    description: "Returns the smallest integer greater than or equal to x.",
    parameters: [
      { name: "x", type: "number | array", description: "Input value" },
    ],
    returns: "Ceiling value",
    examples: [
      { code: "ceil(3.2)", result: "4" },
      { code: "ceil(-3.7)", result: "-3" },
    ],
    seeAlso: ["floor", "round", "trunc"],
  },
  round: {
    name: "round",
    category: "Rounding",
    synopsis: "round(x, [decimals])",
    description:
      "Rounds x to the nearest integer, or to specified decimal places.",
    parameters: [
      { name: "x", type: "number | array", description: "Input value" },
      {
        name: "decimals",
        type: "number",
        description: "Decimal places",
        optional: true,
        default: "0",
      },
    ],
    returns: "Rounded value",
    examples: [
      { code: "round(3.5)", result: "4" },
      { code: "round(3.14159, 2)", result: "3.14" },
    ],
    seeAlso: ["floor", "ceil", "trunc"],
  },
  trunc: {
    name: "trunc",
    category: "Rounding",
    synopsis: "trunc(x)",
    description:
      "Returns the integer part of x, removing any fractional digits.",
    parameters: [
      { name: "x", type: "number | array", description: "Input value" },
    ],
    returns: "Truncated value",
    examples: [
      { code: "trunc(3.7)", result: "3" },
      { code: "trunc(-3.7)", result: "-3" },
    ],
    seeAlso: ["floor", "ceil", "round"],
  },

  // Array operations
  range: {
    name: "range",
    category: "Arrays",
    synopsis: "range(start, end, [step]) or start:end:step",
    description:
      "Creates an array of numbers from start to end (inclusive) with optional step.",
    parameters: [
      { name: "start", type: "number", description: "Starting value" },
      { name: "end", type: "number", description: "Ending value (inclusive)" },
      {
        name: "step",
        type: "number",
        description: "Step size",
        optional: true,
        default: "1",
      },
    ],
    returns: "Array of numbers",
    examples: [
      { code: "range(1, 5)", result: "[1, 2, 3, 4, 5]" },
      { code: "1:5", result: "[1, 2, 3, 4, 5]", description: "Operator form" },
      {
        code: "0:10:2",
        result: "[0, 2, 4, 6, 8, 10]",
        description: "With step",
      },
    ],
    seeAlso: ["linspace", "len"],
  },
  linspace: {
    name: "linspace",
    category: "Arrays",
    synopsis: "linspace(start, end, n)",
    description:
      "Creates an array of n evenly spaced numbers from start to end.",
    parameters: [
      { name: "start", type: "number", description: "Starting value" },
      { name: "end", type: "number", description: "Ending value" },
      { name: "n", type: "number", description: "Number of points" },
    ],
    returns: "Array of n evenly spaced numbers",
    examples: [
      { code: "linspace(0, 1, 5)", result: "[0, 0.25, 0.5, 0.75, 1]" },
      {
        code: "linspace(0, 2*pi, 100)",
        description: "100 points from 0 to 2π",
      },
    ],
    seeAlso: ["range"],
  },
  len: {
    name: "len",
    category: "Arrays",
    synopsis: "len(x)",
    description: "Returns the length of an array or string.",
    parameters: [
      {
        name: "x",
        type: "array | string",
        description: "Input array or string",
      },
    ],
    returns: "Length as number",
    examples: [
      { code: "len([1, 2, 3])", result: "3" },
      { code: 'len("hello")', result: "5" },
    ],
    seeAlso: ["size"],
  },
  sum: {
    name: "sum",
    category: "Statistics",
    synopsis: "sum(array)",
    description: "Returns the sum of all elements in the array.",
    parameters: [
      { name: "array", type: "array", description: "Array of numbers" },
    ],
    returns: "Sum of elements",
    examples: [
      { code: "sum([1, 2, 3, 4, 5])", result: "15" },
      { code: "sum(1:100)", result: "5050" },
    ],
    seeAlso: ["prod", "mean"],
  },
  prod: {
    name: "prod",
    category: "Statistics",
    synopsis: "prod(array)",
    description: "Returns the product of all elements in the array.",
    parameters: [
      { name: "array", type: "array", description: "Array of numbers" },
    ],
    returns: "Product of elements",
    examples: [
      { code: "prod([1, 2, 3, 4])", result: "24" },
      { code: "prod(1:5)", result: "120", description: "Same as factorial(5)" },
    ],
    seeAlso: ["sum", "factorial"],
  },
  mean: {
    name: "mean",
    category: "Statistics",
    synopsis: "mean(array)",
    description: "Returns the arithmetic mean (average) of the array elements.",
    parameters: [
      { name: "array", type: "array", description: "Array of numbers" },
    ],
    returns: "Mean value",
    examples: [
      { code: "mean([1, 2, 3, 4, 5])", result: "3" },
      { code: "mean([10, 20, 30])", result: "20" },
    ],
    seeAlso: ["median", "std", "variance"],
  },
  median: {
    name: "median",
    category: "Statistics",
    synopsis: "median(array)",
    description: "Returns the median (middle value) of the sorted array.",
    parameters: [
      { name: "array", type: "array", description: "Array of numbers" },
    ],
    returns: "Median value",
    examples: [
      { code: "median([1, 3, 5])", result: "3" },
      {
        code: "median([1, 2, 3, 4])",
        result: "2.5",
        description: "Average of middle two",
      },
    ],
    seeAlso: ["mean"],
  },
  variance: {
    name: "variance",
    category: "Statistics",
    synopsis: "variance(array)",
    description: "Returns the variance of the array (population variance).",
    parameters: [
      { name: "array", type: "array", description: "Array of numbers" },
    ],
    returns: "Variance",
    examples: [{ code: "variance([1, 2, 3, 4, 5])", result: "2" }],
    seeAlso: ["std", "mean"],
  },
  std: {
    name: "std",
    category: "Statistics",
    synopsis: "std(array)",
    description: "Returns the standard deviation of the array.",
    parameters: [
      { name: "array", type: "array", description: "Array of numbers" },
    ],
    returns: "Standard deviation",
    examples: [{ code: "std([1, 2, 3, 4, 5])", result: "1.414..." }],
    seeAlso: ["variance", "mean"],
  },
  min: {
    name: "min",
    category: "Statistics",
    synopsis: "min(array) or min(a, b, ...)",
    description: "Returns the minimum value from an array or set of arguments.",
    parameters: [
      {
        name: "values",
        type: "array | numbers",
        description: "Array or multiple values",
      },
    ],
    returns: "Minimum value",
    examples: [
      { code: "min([3, 1, 4, 1, 5])", result: "1" },
      { code: "min(3, 1, 4)", result: "1" },
    ],
    seeAlso: ["max"],
  },
  max: {
    name: "max",
    category: "Statistics",
    synopsis: "max(array) or max(a, b, ...)",
    description: "Returns the maximum value from an array or set of arguments.",
    parameters: [
      {
        name: "values",
        type: "array | numbers",
        description: "Array or multiple values",
      },
    ],
    returns: "Maximum value",
    examples: [
      { code: "max([3, 1, 4, 1, 5])", result: "5" },
      { code: "max(3, 1, 4)", result: "4" },
    ],
    seeAlso: ["min"],
  },
  sort: {
    name: "sort",
    category: "Arrays",
    synopsis: "sort(array)",
    description: "Returns a sorted copy of the array in ascending order.",
    parameters: [
      { name: "array", type: "array", description: "Array to sort" },
    ],
    returns: "Sorted array",
    examples: [{ code: "sort([3, 1, 4, 1, 5])", result: "[1, 1, 3, 4, 5]" }],
    seeAlso: ["reverse"],
  },
  reverse: {
    name: "reverse",
    category: "Arrays",
    synopsis: "reverse(array)",
    description: "Returns a reversed copy of the array.",
    parameters: [
      { name: "array", type: "array", description: "Array to reverse" },
    ],
    returns: "Reversed array",
    examples: [{ code: "reverse([1, 2, 3])", result: "[3, 2, 1]" }],
    seeAlso: ["sort"],
  },
  concat: {
    name: "concat",
    category: "Arrays",
    synopsis: "concat(a, b, ...)",
    description: "Concatenates multiple arrays into a single array.",
    parameters: [
      { name: "arrays", type: "arrays", description: "Arrays to concatenate" },
    ],
    returns: "Concatenated array",
    examples: [
      { code: "concat([1, 2], [3, 4])", result: "[1, 2, 3, 4]" },
      { code: "concat([1], [2], [3])", result: "[1, 2, 3]" },
    ],
  },

  // Matrix operations
  matrix: {
    name: "matrix",
    category: "Matrices",
    synopsis: "matrix(rows) or [[a, b], [c, d]]",
    description:
      "Creates a matrix from nested arrays. Use MATLAB-style syntax [a, b; c, d] for convenience.",
    parameters: [
      {
        name: "rows",
        type: "array of arrays",
        description: "2D array of values",
      },
    ],
    returns: "Matrix",
    examples: [
      { code: "[[1, 2], [3, 4]]", result: "2×2 matrix" },
      {
        code: "[1, 2; 3, 4]",
        result: "2×2 matrix",
        description: "MATLAB syntax",
      },
    ],
    seeAlso: ["zeros", "ones", "eye"],
  },
  zeros: {
    name: "zeros",
    category: "Matrices",
    synopsis: "zeros(rows, cols)",
    description: "Creates a matrix of zeros with the specified dimensions.",
    parameters: [
      { name: "rows", type: "number", description: "Number of rows" },
      { name: "cols", type: "number", description: "Number of columns" },
    ],
    returns: "Matrix of zeros",
    examples: [{ code: "zeros(2, 3)", result: "2×3 matrix of zeros" }],
    seeAlso: ["ones", "eye"],
  },
  ones: {
    name: "ones",
    category: "Matrices",
    synopsis: "ones(rows, cols)",
    description: "Creates a matrix of ones with the specified dimensions.",
    parameters: [
      { name: "rows", type: "number", description: "Number of rows" },
      { name: "cols", type: "number", description: "Number of columns" },
    ],
    returns: "Matrix of ones",
    examples: [{ code: "ones(2, 3)", result: "2×3 matrix of ones" }],
    seeAlso: ["zeros", "eye"],
  },
  eye: {
    name: "eye",
    category: "Matrices",
    synopsis: "eye(n)",
    description: "Creates an n×n identity matrix.",
    parameters: [
      { name: "n", type: "number", description: "Size of the matrix" },
    ],
    returns: "Identity matrix",
    examples: [{ code: "eye(3)", result: "3×3 identity matrix" }],
    seeAlso: ["zeros", "ones", "diag"],
  },
  diag: {
    name: "diag",
    category: "Matrices",
    synopsis: "diag(v) or diag(M)",
    description:
      "Creates a diagonal matrix from a vector, or extracts the diagonal from a matrix.",
    parameters: [
      {
        name: "v or M",
        type: "array | matrix",
        description: "Vector or matrix",
      },
    ],
    returns: "Diagonal matrix or diagonal vector",
    examples: [
      { code: "diag([1, 2, 3])", result: "3×3 diagonal matrix" },
      {
        code: "diag([[1,2],[3,4]])",
        result: "[1, 4]",
        description: "Extract diagonal",
      },
    ],
    seeAlso: ["eye", "trace"],
  },
  transpose: {
    name: "transpose",
    category: "Matrices",
    synopsis: "transpose(M) or M'",
    description: "Returns the transpose of matrix M.",
    parameters: [{ name: "M", type: "matrix", description: "Input matrix" }],
    returns: "Transposed matrix",
    examples: [
      { code: "transpose([[1,2],[3,4]])", result: "[[1,3],[2,4]]" },
      {
        code: "[[1,2],[3,4]]'",
        result: "[[1,3],[2,4]]",
        description: "Operator form",
      },
    ],
    seeAlso: ["inv", "det"],
  },
  det: {
    name: "det",
    category: "Matrices",
    synopsis: "det(M)",
    description: "Returns the determinant of a square matrix.",
    parameters: [{ name: "M", type: "matrix", description: "Square matrix" }],
    returns: "Determinant value",
    examples: [
      { code: "det([[1,2],[3,4]])", result: "-2" },
      { code: "det(eye(3))", result: "1" },
    ],
    seeAlso: ["inv", "trace"],
  },
  trace: {
    name: "trace",
    category: "Matrices",
    synopsis: "trace(M)",
    description:
      "Returns the trace (sum of diagonal elements) of a square matrix.",
    parameters: [{ name: "M", type: "matrix", description: "Square matrix" }],
    returns: "Trace value",
    examples: [
      { code: "trace([[1,2],[3,4]])", result: "5" },
      { code: "trace(eye(5))", result: "5" },
    ],
    seeAlso: ["det", "diag"],
  },
  inv: {
    name: "inv",
    category: "Matrices",
    synopsis: "inv(M)",
    description: "Returns the inverse of a square matrix.",
    parameters: [
      { name: "M", type: "matrix", description: "Square invertible matrix" },
    ],
    returns: "Inverse matrix",
    examples: [
      { code: "inv([[1,2],[3,4]])", result: "[[-2,1],[1.5,-0.5]]" },
      { code: "A * inv(A)", result: "eye(n)", description: "Identity" },
    ],
    seeAlso: ["det", "transpose"],
  },
  size: {
    name: "size",
    category: "Matrices",
    synopsis: "size(M)",
    description: "Returns the dimensions of a matrix as [rows, cols].",
    parameters: [{ name: "M", type: "matrix", description: "Input matrix" }],
    returns: "[rows, cols]",
    examples: [{ code: "size([[1,2,3],[4,5,6]])", result: "[2, 3]" }],
    seeAlso: ["len"],
  },

  // Complex numbers
  re: {
    name: "re",
    category: "Complex",
    synopsis: "re(z)",
    description: "Returns the real part of a complex number.",
    parameters: [{ name: "z", type: "complex", description: "Complex number" }],
    returns: "Real part",
    examples: [{ code: "re(3 + 4i)", result: "3" }],
    seeAlso: ["im", "conj", "abs"],
  },
  im: {
    name: "im",
    category: "Complex",
    synopsis: "im(z)",
    description: "Returns the imaginary part of a complex number.",
    parameters: [{ name: "z", type: "complex", description: "Complex number" }],
    returns: "Imaginary part",
    examples: [{ code: "im(3 + 4i)", result: "4" }],
    seeAlso: ["re", "conj"],
  },
  conj: {
    name: "conj",
    category: "Complex",
    synopsis: "conj(z)",
    description: "Returns the complex conjugate of z.",
    parameters: [{ name: "z", type: "complex", description: "Complex number" }],
    returns: "Complex conjugate",
    examples: [{ code: "conj(3 + 4i)", result: "3 - 4i" }],
    seeAlso: ["re", "im"],
  },
  arg: {
    name: "arg",
    category: "Complex",
    synopsis: "arg(z)",
    description: "Returns the argument (angle) of a complex number in radians.",
    parameters: [{ name: "z", type: "complex", description: "Complex number" }],
    returns: "Angle in radians",
    examples: [
      { code: "arg(1 + i)", result: "π/4" },
      { code: "arg(-1)", result: "π" },
    ],
    seeAlso: ["abs", "polar"],
  },
  polar: {
    name: "polar",
    category: "Complex",
    synopsis: "polar(r, theta)",
    description:
      "Creates a complex number from polar coordinates (magnitude and angle).",
    parameters: [
      { name: "r", type: "number", description: "Magnitude" },
      { name: "theta", type: "number", description: "Angle in radians" },
    ],
    returns: "Complex number",
    examples: [
      { code: "polar(1, pi/4)", result: "0.707 + 0.707i" },
      { code: "polar(2, pi)", result: "-2" },
    ],
    seeAlso: ["arg", "abs"],
  },

  // Combinatorics
  factorial: {
    name: "factorial",
    category: "Combinatorics",
    synopsis: "factorial(n)",
    description: "Returns n! = n × (n-1) × ... × 1.",
    parameters: [
      { name: "n", type: "number", description: "Non-negative integer" },
    ],
    returns: "Factorial of n",
    examples: [
      { code: "factorial(5)", result: "120" },
      { code: "factorial(0)", result: "1" },
    ],
    seeAlso: ["nCr", "nPr"],
  },
  nCr: {
    name: "nCr",
    category: "Combinatorics",
    synopsis: "nCr(n, r)",
    description:
      'Returns "n choose r" - the number of ways to choose r items from n items (combinations).',
    parameters: [
      { name: "n", type: "number", description: "Total items" },
      { name: "r", type: "number", description: "Items to choose" },
    ],
    returns: "Number of combinations",
    examples: [
      { code: "nCr(5, 2)", result: "10" },
      { code: "nCr(10, 3)", result: "120" },
    ],
    seeAlso: ["nPr", "factorial"],
  },
  nPr: {
    name: "nPr",
    category: "Combinatorics",
    synopsis: "nPr(n, r)",
    description:
      "Returns the number of permutations of r items from n items (order matters).",
    parameters: [
      { name: "n", type: "number", description: "Total items" },
      { name: "r", type: "number", description: "Items to arrange" },
    ],
    returns: "Number of permutations",
    examples: [
      { code: "nPr(5, 2)", result: "20" },
      { code: "nPr(10, 3)", result: "720" },
    ],
    seeAlso: ["nCr", "factorial"],
  },
  gcd: {
    name: "gcd",
    category: "Combinatorics",
    synopsis: "gcd(a, b)",
    description: "Returns the greatest common divisor of a and b.",
    parameters: [
      { name: "a", type: "number", description: "First integer" },
      { name: "b", type: "number", description: "Second integer" },
    ],
    returns: "GCD of a and b",
    examples: [
      { code: "gcd(12, 8)", result: "4" },
      { code: "gcd(17, 13)", result: "1" },
    ],
    seeAlso: ["lcm"],
  },
  lcm: {
    name: "lcm",
    category: "Combinatorics",
    synopsis: "lcm(a, b)",
    description: "Returns the least common multiple of a and b.",
    parameters: [
      { name: "a", type: "number", description: "First integer" },
      { name: "b", type: "number", description: "Second integer" },
    ],
    returns: "LCM of a and b",
    examples: [
      { code: "lcm(4, 6)", result: "12" },
      { code: "lcm(3, 5)", result: "15" },
    ],
    seeAlso: ["gcd"],
  },

  // Plotting
  plot: {
    name: "plot",
    category: "Plotting",
    synopsis: "plot(x, y, [label])",
    description:
      "Creates a 2D line plot. Use after calling to add multiple series to the same plot.",
    parameters: [
      { name: "x", type: "array", description: "X coordinates" },
      { name: "y", type: "array", description: "Y coordinates" },
      {
        name: "label",
        type: "string",
        description: "Series label for legend",
        optional: true,
      },
    ],
    returns: "Plot object",
    examples: [
      {
        code: "x = linspace(0, 2*pi, 100); plot(x, sin(x))",
        description: "Sine wave",
      },
      {
        code: 'plot(x, sin(x), "sin"); plot(x, cos(x), "cos")',
        description: "Multiple series",
      },
    ],
    seeAlso: ["scatter", "histogram", "odeplot"],
  },
  scatter: {
    name: "scatter",
    category: "Plotting",
    synopsis: "scatter(x, y, [label])",
    description: "Creates a scatter plot with markers at each (x, y) point.",
    parameters: [
      { name: "x", type: "array", description: "X coordinates" },
      { name: "y", type: "array", description: "Y coordinates" },
      {
        name: "label",
        type: "string",
        description: "Series label for legend",
        optional: true,
      },
    ],
    returns: "Plot object",
    examples: [
      { code: "scatter([1,2,3,4], [1,4,9,16])", description: "Square numbers" },
    ],
    seeAlso: ["plot"],
  },
  histogram: {
    name: "histogram",
    category: "Plotting",
    synopsis: "histogram(data, [bins], [label])",
    description: "Creates a histogram showing the distribution of data.",
    parameters: [
      { name: "data", type: "array", description: "Data values" },
      {
        name: "bins",
        type: "number",
        description: "Number of bins",
        optional: true,
        default: "10",
      },
      { name: "label", type: "string", description: "Label", optional: true },
    ],
    returns: "Plot object",
    examples: [
      { code: "histogram(random(1000))", description: "Random distribution" },
    ],
    seeAlso: ["plot"],
  },
  odesolve: {
    name: "odesolve",
    category: "ODE",
    synopsis: "odesolve(f, tspan, y0, [options])",
    description:
      "Solves an ordinary differential equation dy/dt = f(t, y) using numerical methods.",
    parameters: [
      {
        name: "f",
        type: "function",
        description: "Function f(t, y) returning dy/dt",
      },
      {
        name: "tspan",
        type: "[t0, tf]",
        description: "Time span [start, end]",
      },
      {
        name: "y0",
        type: "number | array",
        description: "Initial condition(s)",
      },
      {
        name: "options",
        type: "object",
        description: "Options object",
        optional: true,
      },
    ],
    returns: "[t, y] arrays of times and solutions",
    examples: [
      {
        code: 'f = fn(t,y) => -y; [t,y] = odesolve(f, [0,5], 1, {method:"rk4"})',
        description: "Exponential decay",
      },
      {
        code: "f = fn(t,y) => [y[1], -y[0]]; odesolve(f, [0,2*pi], [1,0])",
        description: "Harmonic oscillator",
      },
    ],
    seeAlso: ["odeplot"],
  },
  odeplot: {
    name: "odeplot",
    category: "ODE",
    synopsis: "odeplot(f, tspan, y0, [options])",
    description: "Solves an ODE and automatically plots the solution.",
    parameters: [
      {
        name: "f",
        type: "function",
        description: "Function f(t, y) returning dy/dt",
      },
      {
        name: "tspan",
        type: "[t0, tf]",
        description: "Time span [start, end]",
      },
      {
        name: "y0",
        type: "number | array",
        description: "Initial condition(s)",
      },
      {
        name: "options",
        type: "object",
        description: "Options",
        optional: true,
      },
    ],
    returns: "Plot object",
    examples: [
      {
        code: "odeplot(fn(t,y) => -y, [0,5], 1)",
        description: "Plot exponential decay",
      },
    ],
    seeAlso: ["odesolve", "plot"],
  },

  // Utility
  random: {
    name: "random",
    category: "Random",
    synopsis: "random([n])",
    description:
      "Returns a random number between 0 and 1, or an array of n random numbers.",
    parameters: [
      {
        name: "n",
        type: "number",
        description: "Number of random values",
        optional: true,
      },
    ],
    returns: "Random value(s)",
    examples: [
      { code: "random()", description: "Single random number" },
      {
        code: "random(5)",
        result: "[0.2, 0.7, ...]",
        description: "Array of 5 random numbers",
      },
    ],
    seeAlso: ["randint"],
  },
  randint: {
    name: "randint",
    category: "Random",
    synopsis: "randint(min, max, [n])",
    description: "Returns a random integer between min and max (inclusive).",
    parameters: [
      { name: "min", type: "number", description: "Minimum value" },
      { name: "max", type: "number", description: "Maximum value" },
      {
        name: "n",
        type: "number",
        description: "Number of values",
        optional: true,
      },
    ],
    returns: "Random integer(s)",
    examples: [
      { code: "randint(1, 6)", description: "Dice roll" },
      { code: "randint(1, 100, 10)", description: "10 random integers 1-100" },
    ],
    seeAlso: ["random"],
  },

  // ── MATLAB Plotting Extensions ───────────────────────────────
  bar: {
    name: "bar",
    category: "Plotting",
    synopsis: "bar(x, y, [label])",
    description:
      "Creates a bar chart. Each element of x defines a category position and y defines the bar height.",
    parameters: [
      { name: "x", type: "array", description: "Category positions or labels" },
      { name: "y", type: "array", description: "Bar heights" },
      {
        name: "label",
        type: "string",
        description: "Series label",
        optional: true,
      },
    ],
    returns: "Plot object",
    examples: [
      {
        code: "bar([1,2,3,4], [10,20,15,25])",
        description: "Simple bar chart",
      },
      {
        code: 'bar([1,2,3], [5,8,3], "Sales")',
        description: "Labeled bar chart",
      },
    ],
    seeAlso: ["plot", "histogram", "stem"],
  },
  stem: {
    name: "stem",
    category: "Plotting",
    synopsis: "stem(x, y, [label])",
    description:
      "Creates a stem plot (lollipop chart). Draws vertical lines from the baseline to each data point with a marker at the top.",
    parameters: [
      { name: "x", type: "array", description: "X coordinates" },
      { name: "y", type: "array", description: "Y coordinates" },
      {
        name: "label",
        type: "string",
        description: "Series label",
        optional: true,
      },
    ],
    returns: "Plot object",
    examples: [
      {
        code: "stem([1,2,3,4,5], [1,4,9,16,25])",
        description: "Stem plot of squares",
      },
    ],
    seeAlso: ["plot", "bar", "scatter"],
  },
  plot3: {
    name: "plot3",
    category: "Plotting",
    synopsis: "plot3(x, y, z, [label])",
    description:
      "Creates a 3D line plot. Plots a curve through the 3D points defined by x, y, and z arrays.",
    parameters: [
      { name: "x", type: "array", description: "X coordinates" },
      { name: "y", type: "array", description: "Y coordinates" },
      { name: "z", type: "array", description: "Z coordinates" },
      {
        name: "label",
        type: "string",
        description: "Series label",
        optional: true,
      },
    ],
    returns: "Plot object",
    examples: [
      {
        code: "t = linspace(0, 4*pi, 200); plot3(cos(t), sin(t), t)",
        description: "3D helix",
      },
    ],
    seeAlso: ["plot", "surf", "mesh"],
  },
  surf: {
    name: "surf",
    category: "Plotting",
    synopsis: "surf(X, Y, Z, [label])",
    description:
      "Creates a 3D surface plot. X, Y, and Z are 2D matrices (typically created with meshgrid).",
    parameters: [
      { name: "X", type: "matrix", description: "X grid matrix" },
      { name: "Y", type: "matrix", description: "Y grid matrix" },
      { name: "Z", type: "matrix", description: "Z values matrix" },
      {
        name: "label",
        type: "string",
        description: "Surface label",
        optional: true,
      },
    ],
    returns: "Plot object",
    examples: [
      {
        code: "[X,Y] = meshgrid(linspace(-2,2,30), linspace(-2,2,30)); surf(X, Y, sin(X).*cos(Y))",
        description: "Surface plot of sin(x)·cos(y)",
      },
    ],
    seeAlso: ["mesh", "contour", "meshgrid", "plot3"],
  },
  mesh: {
    name: "mesh",
    category: "Plotting",
    synopsis: "mesh(X, Y, Z, [label])",
    description:
      "Creates a 3D wireframe mesh plot. Similar to surf but renders as a wire mesh instead of a filled surface.",
    parameters: [
      { name: "X", type: "matrix", description: "X grid matrix" },
      { name: "Y", type: "matrix", description: "Y grid matrix" },
      { name: "Z", type: "matrix", description: "Z values matrix" },
      { name: "label", type: "string", description: "Label", optional: true },
    ],
    returns: "Plot object",
    examples: [
      {
        code: "[X,Y] = meshgrid(linspace(-3,3,25)); mesh(X, Y, X.^2 + Y.^2)",
        description: "Mesh plot of paraboloid",
      },
    ],
    seeAlso: ["surf", "contour", "meshgrid"],
  },
  contour: {
    name: "contour",
    category: "Plotting",
    synopsis: "contour(X, Y, Z, [levels])",
    description:
      "Creates a 2D contour plot showing isolines of the Z values over the X-Y plane.",
    parameters: [
      { name: "X", type: "matrix", description: "X grid matrix" },
      { name: "Y", type: "matrix", description: "Y grid matrix" },
      { name: "Z", type: "matrix", description: "Z values matrix" },
      {
        name: "levels",
        type: "number",
        description: "Number of contour levels",
        optional: true,
      },
    ],
    returns: "Plot object",
    examples: [
      {
        code: "[X,Y] = meshgrid(linspace(-2,2,40)); contour(X, Y, X.^2 + Y.^2)",
        description: "Contour plot of paraboloid",
      },
    ],
    seeAlso: ["surf", "mesh", "meshgrid"],
  },
  meshgrid: {
    name: "meshgrid",
    category: "Plotting",
    synopsis: "meshgrid(x, y)",
    description:
      "Creates 2D grid matrices from two 1D coordinate vectors. Returns [X, Y] where X replicates the x vector as rows and Y replicates the y vector as columns. Essential for 3D plotting functions.",
    parameters: [
      { name: "x", type: "array", description: "X coordinates vector" },
      { name: "y", type: "array", description: "Y coordinates vector" },
    ],
    returns: "[X, Y] grid matrices",
    examples: [
      {
        code: "[X, Y] = meshgrid([1,2,3], [4,5])",
        description: "Create 2×3 grid",
      },
      {
        code: "[X, Y] = meshgrid(linspace(-1,1,50))",
        description: "Square grid for surface plots",
      },
    ],
    seeAlso: ["surf", "mesh", "contour", "linspace"],
  },
  title: {
    name: "title",
    category: "Plotting",
    synopsis: "title(text)",
    description: "Sets the title of the current plot.",
    parameters: [{ name: "text", type: "string", description: "Title text" }],
    returns: "void",
    examples: [
      { code: 'plot(x, y); title("Sine Wave")', description: "Add plot title" },
    ],
    seeAlso: ["xlabel", "ylabel", "zlabel"],
  },
  xlabel: {
    name: "xlabel",
    category: "Plotting",
    synopsis: "xlabel(text)",
    description: "Sets the label for the X axis of the current plot.",
    parameters: [{ name: "text", type: "string", description: "X axis label" }],
    returns: "void",
    examples: [{ code: 'xlabel("Time (s)")', description: "Label x axis" }],
    seeAlso: ["ylabel", "zlabel", "title"],
  },
  ylabel: {
    name: "ylabel",
    category: "Plotting",
    synopsis: "ylabel(text)",
    description: "Sets the label for the Y axis of the current plot.",
    parameters: [{ name: "text", type: "string", description: "Y axis label" }],
    returns: "void",
    examples: [{ code: 'ylabel("Amplitude")', description: "Label y axis" }],
    seeAlso: ["xlabel", "zlabel", "title"],
  },
  zlabel: {
    name: "zlabel",
    category: "Plotting",
    synopsis: "zlabel(text)",
    description: "Sets the label for the Z axis of 3D plots.",
    parameters: [{ name: "text", type: "string", description: "Z axis label" }],
    returns: "void",
    examples: [
      { code: 'zlabel("Height")', description: "Label z axis on a 3D plot" },
    ],
    seeAlso: ["xlabel", "ylabel", "title"],
  },
  xlim: {
    name: "xlim",
    category: "Plotting",
    synopsis: "xlim([xmin, xmax])",
    description: "Sets the X axis limits for the current plot.",
    parameters: [
      {
        name: "limits",
        type: "[number, number]",
        description: "Min and max values for X axis",
      },
    ],
    returns: "void",
    examples: [
      { code: "xlim([0, 10])", description: "Set x range from 0 to 10" },
    ],
    seeAlso: ["ylim", "axis"],
  },
  ylim: {
    name: "ylim",
    category: "Plotting",
    synopsis: "ylim([ymin, ymax])",
    description: "Sets the Y axis limits for the current plot.",
    parameters: [
      {
        name: "limits",
        type: "[number, number]",
        description: "Min and max values for Y axis",
      },
    ],
    returns: "void",
    examples: [
      { code: "ylim([-1, 1])", description: "Set y range from -1 to 1" },
    ],
    seeAlso: ["xlim", "axis"],
  },
  axis: {
    name: "axis",
    category: "Plotting",
    synopsis: "axis([xmin, xmax, ymin, ymax])",
    description: "Sets both X and Y axis limits simultaneously.",
    parameters: [
      {
        name: "limits",
        type: "[number, number, number, number]",
        description: "Axis limits [xmin, xmax, ymin, ymax]",
      },
    ],
    returns: "void",
    examples: [
      {
        code: "axis([0, 2*pi, -1, 1])",
        description: "Set axis limits for a sine wave plot",
      },
    ],
    seeAlso: ["xlim", "ylim"],
  },

  // ── Advanced Plotting ────────────────────────────────────────
  parametric: {
    name: "parametric",
    category: "Plotting",
    synopsis: "parametric(fx, fy, tRange, [options])",
    description:
      "Creates a parametric curve plot. Evaluates fx(t) and fy(t) over the given t range to produce x and y coordinates.",
    parameters: [
      { name: "fx", type: "function", description: "Function handle for x(t)" },
      { name: "fy", type: "function", description: "Function handle for y(t)" },
      {
        name: "tRange",
        type: "array",
        description: "Parameter values (e.g. linspace(0, 2*pi, 200))",
      },
      {
        name: "options",
        type: "string|struct",
        description: "Label string or options struct with title/label",
        optional: true,
      },
    ],
    returns: "Plot object",
    examples: [
      {
        code: "t = linspace(0, 2*pi, 200); parametric(@(t) cos(t), @(t) sin(t), t)",
        description: "Unit circle",
      },
      {
        code: "t = linspace(0, 4*pi, 500); parametric(@(t) t.*cos(t), @(t) t.*sin(t), t, 'Spiral')",
        description: "Archimedean spiral",
      },
    ],
    seeAlso: ["plot", "plot3", "scatter"],
  },
  quiver: {
    name: "quiver",
    category: "Plotting",
    synopsis: "quiver(X, Y, U, V, [title])",
    description:
      "Creates a 2D vector field (quiver) plot. Draws arrows at positions (X,Y) with components (U,V).",
    parameters: [
      {
        name: "X",
        type: "array|matrix",
        description: "X positions of arrow bases",
      },
      {
        name: "Y",
        type: "array|matrix",
        description: "Y positions of arrow bases",
      },
      {
        name: "U",
        type: "array|matrix",
        description: "X components of arrows",
      },
      {
        name: "V",
        type: "array|matrix",
        description: "Y components of arrows",
      },
      {
        name: "title",
        type: "string",
        description: "Plot title",
        optional: true,
      },
    ],
    returns: "Plot object",
    examples: [
      {
        code: "[X,Y] = meshgrid(linspace(-2,2,10)); quiver(X, Y, -Y, X, 'Rotation field')",
        description: "Rotation vector field",
      },
    ],
    seeAlso: ["vectorfield", "streamplot"],
  },
  vectorfield: {
    name: "vectorfield",
    category: "Plotting",
    synopsis: "vectorfield(fn, xRange, yRange, [title])",
    description:
      "Creates a vector field plot from a function fn(x,y) that returns [u, v]. The function is evaluated on the grid defined by xRange and yRange.",
    parameters: [
      {
        name: "fn",
        type: "function",
        description: "Function handle @(x,y) returning [u, v]",
      },
      { name: "xRange", type: "array", description: "X coordinate values" },
      { name: "yRange", type: "array", description: "Y coordinate values" },
      {
        name: "title",
        type: "string",
        description: "Plot title",
        optional: true,
      },
    ],
    returns: "Plot object",
    examples: [
      {
        code: "vectorfield(@(x,y) [-y, x], linspace(-2,2,12), linspace(-2,2,12))",
        description: "Rotation field",
      },
    ],
    seeAlso: ["quiver", "streamplot"],
  },
  streamplot: {
    name: "streamplot",
    category: "Plotting",
    synopsis: "streamplot(fn, xRange, yRange, [title])",
    description:
      "Creates a streamline plot from a vector field function fn(x,y) that returns [u, v]. Streamlines show the flow direction by integrating from seed points.",
    parameters: [
      {
        name: "fn",
        type: "function",
        description: "Function handle @(x,y) returning [u, v]",
      },
      {
        name: "xRange",
        type: "array",
        description: "X coordinate range [min, max] or array",
      },
      {
        name: "yRange",
        type: "array",
        description: "Y coordinate range [min, max] or array",
      },
      {
        name: "title",
        type: "string",
        description: "Plot title",
        optional: true,
      },
    ],
    returns: "Plot object",
    examples: [
      {
        code: "streamplot(@(x,y) [-y, x], linspace(-2,2,10), linspace(-2,2,10))",
        description: "Circular streamlines",
      },
    ],
    seeAlso: ["vectorfield", "quiver"],
  },
  boxplot: {
    name: "boxplot",
    category: "Plotting",
    synopsis: "boxplot(data1, [data2, ...], [title])",
    description:
      "Creates a box-and-whisker plot showing the distribution of one or more datasets. Shows median, quartiles, and outliers.",
    parameters: [
      {
        name: "data",
        type: "array",
        description: "Data values for each group",
      },
      {
        name: "title",
        type: "string",
        description: "Plot title",
        optional: true,
      },
    ],
    returns: "Plot object",
    examples: [
      {
        code: "boxplot(randn(1,100), randn(1,100)*2 + 3)",
        description: "Compare two distributions",
      },
      {
        code: "boxplot([1,2,3,4,5,6,7,8,20], 'Outlier example')",
        description: "Box plot with outlier",
      },
    ],
    seeAlso: ["violinplot", "histogram"],
  },
  violinplot: {
    name: "violinplot",
    category: "Plotting",
    synopsis: "violinplot(data1, [data2, ...], [title])",
    description:
      "Creates a violin plot showing the probability density of data. Combines a box plot with a kernel density estimate on each side.",
    parameters: [
      {
        name: "data",
        type: "array",
        description: "Data values for each group",
      },
      {
        name: "title",
        type: "string",
        description: "Plot title",
        optional: true,
      },
    ],
    returns: "Plot object",
    examples: [
      {
        code: "violinplot(randn(1,200), randn(1,200)*0.5 + 2)",
        description: "Compare distributions with violin plots",
      },
    ],
    seeAlso: ["boxplot", "histogram"],
  },
  layout: {
    name: "layout",
    category: "Plotting",
    synopsis: "layout(rows, cols, plot1, plot2, ...)",
    description:
      "Creates a subplot grid layout with the specified number of rows and columns. Each subsequent argument is a plot to place in the grid, filled left-to-right, top-to-bottom.",
    parameters: [
      { name: "rows", type: "number", description: "Number of grid rows" },
      { name: "cols", type: "number", description: "Number of grid columns" },
      {
        name: "plots",
        type: "plot",
        description: "Plot objects to arrange in the grid",
      },
    ],
    returns: "Plot object (subplot)",
    examples: [
      {
        code: "t = linspace(0,2*pi,100); layout(2, 1, plot(t, sin(t)), plot(t, cos(t)))",
        description: "Two plots stacked vertically",
      },
      {
        code: "layout(1, 2, histogram(randn(1,500)), boxplot(randn(1,500)))",
        description: "Side-by-side histogram and boxplot",
      },
    ],
    seeAlso: ["plot", "subplot"],
  },
  annotation: {
    name: "annotation",
    category: "Plotting",
    synopsis: "annotation(text, x, y, [options])",
    description:
      "Creates a text annotation object to add to a plot via annotate_plot(). Displays text at the specified position with an optional arrow.",
    parameters: [
      { name: "text", type: "string", description: "Annotation text" },
      { name: "x", type: "number", description: "X position" },
      { name: "y", type: "number", description: "Y position" },
      {
        name: "options",
        type: "struct",
        description: "Options: showarrow, ax, ay, arrowhead",
        optional: true,
      },
    ],
    returns: "Annotation object",
    examples: [
      {
        code: "a = annotation('Peak', 3.14, 1)",
        description: "Create annotation at (3.14, 1)",
      },
      {
        code: "p = plot(x, y); annotate_plot(p, annotation('Max', 5, 10))",
        description: "Add annotation to plot",
      },
    ],
    seeAlso: ["annotate_plot", "shape"],
  },
  shape: {
    name: "shape",
    category: "Plotting",
    synopsis: "shape(type, x0, y0, x1, y1, [options])",
    description:
      "Creates a shape object (line, rect, or circle) to overlay on a plot via annotate_plot(). The shape extends from (x0,y0) to (x1,y1).",
    parameters: [
      {
        name: "type",
        type: "string",
        description: "Shape type: 'line', 'rect', or 'circle'",
      },
      { name: "x0", type: "number", description: "Start X coordinate" },
      { name: "y0", type: "number", description: "Start Y coordinate" },
      { name: "x1", type: "number", description: "End X coordinate" },
      { name: "y1", type: "number", description: "End Y coordinate" },
      {
        name: "options",
        type: "struct",
        description: "Options: lineColor, fillColor, lineWidth",
        optional: true,
      },
    ],
    returns: "Shape object",
    examples: [
      {
        code: "s = shape('rect', 0, 0, 5, 5)",
        description: "Create a rectangle from (0,0) to (5,5)",
      },
      {
        code: "p = plot(x,y); annotate_plot(p, shape('line', 0, 0, 10, 10))",
        description: "Add diagonal line to plot",
      },
    ],
    seeAlso: ["annotate_plot", "annotation"],
  },
  annotate_plot: {
    name: "annotate_plot",
    category: "Plotting",
    synopsis: "annotate_plot(plotData, ann1, ann2, ...)",
    description:
      "Adds annotations and/or shapes to an existing plot. Pass annotation() or shape() objects as additional arguments.",
    parameters: [
      {
        name: "plotData",
        type: "plot",
        description: "An existing plot object",
      },
      {
        name: "annotations",
        type: "annotation|shape",
        description: "Annotation or shape objects to add",
      },
    ],
    returns: "Plot object with annotations",
    examples: [
      {
        code: "p = plot(x, sin(x)); annotate_plot(p, annotation('Peak', pi/2, 1), shape('line', 0, 0, 2*pi, 0))",
        description: "Add annotation and reference line",
      },
    ],
    seeAlso: ["annotation", "shape", "plot"],
  },
  interactive_plot: {
    name: "interactive_plot",
    category: "Plotting",
    synopsis: "interactive_plot(fn, xRange, sliders, [title])",
    description:
      "Creates an interactive plot with slider controls. The function fn(x, param1, param2, ...) is re-evaluated as slider values change.",
    parameters: [
      {
        name: "fn",
        type: "function",
        description: "Function handle @(x, params...) to plot",
      },
      {
        name: "xRange",
        type: "array",
        description: "X values (e.g. linspace(0, 10, 200))",
      },
      {
        name: "sliders",
        type: "struct",
        description:
          "Slider definitions: struct with fields min, max, step, default",
      },
      {
        name: "title",
        type: "string",
        description: "Plot title",
        optional: true,
      },
    ],
    returns: "Interactive plot object",
    examples: [
      {
        code: "interactive_plot(@(x,a) a*sin(x), linspace(0,2*pi,200), struct('a', struct('min',0.1,'max',5,'step',0.1,'default',1)))",
        description: "Sine wave with amplitude slider",
      },
    ],
    seeAlso: ["plot", "animate"],
  },
  animate: {
    name: "animate",
    category: "Plotting",
    synopsis: "animate(fn, xRange, tRange, [options])",
    description:
      "Creates an animated plot. The function fn(x, t) is evaluated for each time value in tRange, producing animation frames. Includes play/pause controls and a frame scrubber.",
    parameters: [
      {
        name: "fn",
        type: "function",
        description: "Function handle @(x, t) to animate",
      },
      { name: "xRange", type: "array", description: "X values for each frame" },
      {
        name: "tRange",
        type: "array",
        description: "Time/parameter values for animation frames",
      },
      {
        name: "options",
        type: "struct",
        description:
          "Options: fps, frameDuration, transitionDuration, loop, title",
        optional: true,
      },
    ],
    returns: "Animated plot object",
    examples: [
      {
        code: "animate(@(x,t) sin(x - t), linspace(0,2*pi,200), linspace(0,2*pi,30))",
        description: "Traveling sine wave",
      },
      {
        code: "animate(@(x,t) exp(-((x-t).^2)), linspace(-5,5,200), linspace(-3,3,40), struct('fps',20))",
        description: "Moving Gaussian pulse",
      },
    ],
    seeAlso: ["plot", "interactive_plot"],
  },
  export_plot: {
    name: "export_plot",
    category: "Plotting",
    synopsis: "export_plot(plotData, [format], [options])",
    description:
      "Marks a plot for export/download. When rendered, the plot will automatically be exported in the specified format. Supported formats: png, svg, jpeg, webp.",
    parameters: [
      {
        name: "plotData",
        type: "plot",
        description: "An existing plot object",
      },
      {
        name: "format",
        type: "string",
        description: "Export format: 'png', 'svg', 'jpeg', 'webp'",
        optional: true,
        default: "'png'",
      },
      {
        name: "options",
        type: "struct",
        description: "Options: width, height (in pixels)",
        optional: true,
      },
    ],
    returns: "Plot object with export metadata",
    examples: [
      {
        code: "p = plot(x, sin(x)); export_plot(p, 'png')",
        description: "Export plot as PNG",
      },
      {
        code: "export_plot(surf(X,Y,Z), 'svg', struct('width',1200,'height',800))",
        description: "Export 3D surface as high-res SVG",
      },
    ],
    seeAlso: ["plot", "set_theme"],
  },
  set_theme: {
    name: "set_theme",
    category: "Plotting",
    synopsis: "set_theme(name)",
    description:
      "Sets the visual theme for plots. Available themes: default, dark, light, seaborn, ggplot, minimal, presentation, highcontrast.",
    parameters: [{ name: "name", type: "string", description: "Theme name" }],
    returns: "Theme object",
    examples: [
      { code: "set_theme('seaborn')", description: "Use seaborn-style theme" },
      {
        code: "set_theme('presentation')",
        description: "High-contrast theme for slides",
      },
    ],
    seeAlso: ["apply_theme", "define_theme"],
  },
  apply_theme: {
    name: "apply_theme",
    category: "Plotting",
    synopsis: "apply_theme(plotData, themeName)",
    description:
      "Applies a named theme to an existing plot object, controlling colors, backgrounds, and fonts.",
    parameters: [
      {
        name: "plotData",
        type: "plot",
        description: "An existing plot object",
      },
      { name: "themeName", type: "string", description: "Theme name to apply" },
    ],
    returns: "Plot object with theme",
    examples: [
      {
        code: "p = plot(x, sin(x)); apply_theme(p, 'ggplot')",
        description: "Apply ggplot theme to a line plot",
      },
    ],
    seeAlso: ["set_theme", "define_theme"],
  },
  define_theme: {
    name: "define_theme",
    category: "Plotting",
    synopsis: "define_theme(name, options)",
    description:
      "Defines a custom theme with specified colors and styling options.",
    parameters: [
      { name: "name", type: "string", description: "Custom theme name" },
      {
        name: "options",
        type: "struct",
        description:
          "Theme options: paper_bgcolor, plot_bgcolor, font_color, grid_color, colorway",
      },
    ],
    returns: "Theme definition object",
    examples: [
      {
        code: "define_theme('ocean', struct('paper_bgcolor','#001f3f','font_color','#7fdbff'))",
        description: "Create an ocean theme",
      },
    ],
    seeAlso: ["set_theme", "apply_theme"],
  },
  linreg: {
    name: "linreg",
    category: "Curve Fitting",
    synopsis: "linreg(x, y)",
    description:
      "Performs linear regression on data points (x, y). Returns [slope, intercept, R²] where R² is the coefficient of determination.",
    parameters: [
      { name: "x", type: "array", description: "Independent variable values" },
      { name: "y", type: "array", description: "Dependent variable values" },
    ],
    returns: "[slope, intercept, R²]",
    examples: [
      {
        code: "linreg([1,2,3,4,5], [2.1,3.9,6.2,7.8,10.1])",
        description: "Linear regression",
      },
    ],
    seeAlso: ["polyreg", "fit", "autofit", "fitplot", "polyfit"],
  },
  polyreg: {
    name: "polyreg",
    category: "Curve Fitting",
    synopsis: "polyreg(x, y, degree)",
    description:
      "Performs polynomial regression of the given degree. Returns coefficients in descending order (highest degree first, MATLAB convention).",
    parameters: [
      { name: "x", type: "array", description: "Independent variable values" },
      { name: "y", type: "array", description: "Dependent variable values" },
      {
        name: "degree",
        type: "number",
        description: "Polynomial degree (default: 2)",
      },
    ],
    returns: "Array of coefficients [a_n, a_{n-1}, ..., a_1, a_0]",
    examples: [
      {
        code: "polyreg([1,2,3,4], [1,4,9,16], 2)",
        description: "Quadratic regression of x²",
      },
    ],
    seeAlso: ["linreg", "fit", "polyfit", "fitplot"],
  },
  fit: {
    name: "fit",
    category: "Curve Fitting",
    synopsis: "fit(x, y, [type])",
    description:
      "General-purpose curve fitting. Supports types: 'linear', 'poly1'-'poly9', 'exp', 'power'. Returns a descriptive string with the fitted equation and R².",
    parameters: [
      { name: "x", type: "array", description: "Independent variable values" },
      { name: "y", type: "array", description: "Dependent variable values" },
      {
        name: "type",
        type: "string",
        description: "Fit type: 'linear', 'poly2', 'exp', 'power'",
        optional: true,
        default: "'linear'",
      },
    ],
    returns: "String describing the fit equation",
    examples: [
      {
        code: "fit([1,2,3,4,5], [2.7,7.4,20.1,54.6,148.4], 'exp')",
        description: "Exponential fit",
      },
      {
        code: "fit([1,2,3,4], [1,4,9,16], 'poly2')",
        description: "Quadratic fit",
      },
    ],
    seeAlso: ["linreg", "polyreg", "autofit", "fitplot"],
  },
  autofit: {
    name: "autofit",
    category: "Curve Fitting",
    synopsis: "autofit(x, y)",
    description:
      "Automatically determines the best curve fit by trying linear, quadratic, and other models. Returns the fit with the highest R².",
    parameters: [
      { name: "x", type: "array", description: "Independent variable values" },
      { name: "y", type: "array", description: "Dependent variable values" },
    ],
    returns: "String describing the best fit",
    examples: [
      {
        code: "autofit([1,2,3,4,5], [1,4,9,16,25])",
        description: "Auto-detect quadratic relationship",
      },
    ],
    seeAlso: ["fit", "linreg", "polyreg", "fitplot"],
  },
  fitplot: {
    name: "fitplot",
    category: "Curve Fitting",
    synopsis: "fitplot(x, y, [type], [title])",
    description:
      "Creates a scatter plot of the data with a fitted curve overlay. Combines data visualization with curve fitting in a single plot. Shows the fit type and R² in the legend.",
    parameters: [
      { name: "x", type: "array", description: "Independent variable values" },
      { name: "y", type: "array", description: "Dependent variable values" },
      {
        name: "type",
        type: "string",
        description: "Fit type: 'linear', 'poly2'-'poly9', 'exp', 'power'",
        optional: true,
        default: "'linear'",
      },
      {
        name: "title",
        type: "string",
        description: "Plot title",
        optional: true,
      },
    ],
    returns: "Plot object with data points and fit curve",
    examples: [
      {
        code: "fitplot([1,2,3,4,5], [2.1,3.9,6.2,7.8,10.1])",
        description: "Linear fit plot",
      },
      {
        code: "fitplot([1,2,3,4], [1,4,9,16], 'poly2', 'Quadratic Fit')",
        description: "Quadratic fit with title",
      },
    ],
    seeAlso: ["fit", "linreg", "polyreg", "scatter", "plot"],
  },
  spline: {
    name: "spline",
    category: "Curve Fitting",
    synopsis: "spline(x, y, xq)",
    description:
      "Performs cubic spline interpolation. Given data points (x, y), evaluates the natural cubic spline at query points xq.",
    parameters: [
      {
        name: "x",
        type: "array",
        description: "Known X values (must be sorted)",
      },
      { name: "y", type: "array", description: "Known Y values" },
      {
        name: "xq",
        type: "array",
        description: "Query X values to interpolate at",
      },
    ],
    returns: "Interpolated Y values at xq",
    examples: [
      {
        code: "spline([0,1,2,3], [0,1,0,-1], linspace(0,3,50))",
        description: "Smooth interpolation through 4 points",
      },
    ],
    seeAlso: ["interp1", "polyfit", "fit"],
  },

  // ── Numerical Calculus ───────────────────────────────────────
  gradient: {
    name: "gradient",
    category: "Calculus",
    synopsis: "gradient(y, [h])",
    description:
      "Computes the numerical gradient (derivative) of array y. Uses central differences for interior points and forward/backward differences at boundaries.",
    parameters: [
      { name: "y", type: "array", description: "Function values" },
      {
        name: "h",
        type: "number",
        description: "Spacing between points",
        optional: true,
        default: "1",
      },
    ],
    returns: "Array of gradient values",
    examples: [
      {
        code: "gradient([1, 4, 9, 16, 25])",
        result: "[3, 4, 6, 8, 9]",
        description: "Gradient of x²",
      },
      {
        code: "x = linspace(0, 2*pi, 100); gradient(sin(x), x[2]-x[1])",
        description: "Numerical derivative of sin(x)",
      },
    ],
    seeAlso: ["diff", "trapz"],
  },
  diff: {
    name: "diff",
    category: "Calculus",
    synopsis: "diff(x, [n])",
    description:
      "Computes the n-th order differences of array x. First-order differences return x[i+1] - x[i]. Higher orders apply diff recursively.",
    parameters: [
      { name: "x", type: "array", description: "Input array" },
      {
        name: "n",
        type: "number",
        description: "Order of differences",
        optional: true,
        default: "1",
      },
    ],
    returns: "Array of differences (length reduced by n)",
    examples: [
      {
        code: "diff([1, 4, 9, 16])",
        result: "[3, 5, 7]",
        description: "First differences of x²",
      },
      {
        code: "diff([1, 4, 9, 16], 2)",
        result: "[2, 2]",
        description: "Second differences (constant for quadratic)",
      },
    ],
    seeAlso: ["gradient", "cumtrapz"],
  },
  trapz: {
    name: "trapz",
    category: "Calculus",
    synopsis: "trapz(x, y)",
    description:
      "Computes the definite integral of y with respect to x using the trapezoidal rule.",
    parameters: [
      {
        name: "x",
        type: "array",
        description: "X coordinates (sample points)",
      },
      {
        name: "y",
        type: "array",
        description: "Y values (function values at sample points)",
      },
    ],
    returns: "Approximate integral value",
    examples: [
      {
        code: "x = linspace(0, pi, 1000); trapz(x, sin(x))",
        result: "2",
        description: "∫₀π sin(x) dx ≈ 2",
      },
    ],
    seeAlso: ["cumtrapz", "quad", "gradient"],
  },
  cumtrapz: {
    name: "cumtrapz",
    category: "Calculus",
    synopsis: "cumtrapz(x, y)",
    description:
      "Computes the cumulative trapezoidal integral of y with respect to x. Returns an array of running integral values.",
    parameters: [
      { name: "x", type: "array", description: "X coordinates" },
      { name: "y", type: "array", description: "Y values" },
    ],
    returns: "Array of cumulative integral values",
    examples: [
      {
        code: "x = linspace(0, pi, 100); cumtrapz(x, sin(x))",
        description: "Cumulative integral of sin(x) from 0 to π",
      },
    ],
    seeAlso: ["trapz", "gradient"],
  },
  quad: {
    name: "quad",
    category: "Calculus",
    synopsis: "quad(f, a, b)",
    description:
      "Computes the definite integral of function f from a to b using adaptive numerical quadrature.",
    parameters: [
      {
        name: "f",
        type: "function",
        description: "Function to integrate (single argument)",
      },
      { name: "a", type: "number", description: "Lower integration limit" },
      { name: "b", type: "number", description: "Upper integration limit" },
    ],
    returns: "Approximate integral value",
    examples: [
      {
        code: "quad(fn(x) => x^2, 0, 1)",
        result: "0.333...",
        description: "∫₀¹ x² dx = 1/3",
      },
      {
        code: "quad(fn(x) => sin(x), 0, pi)",
        result: "2",
        description: "∫₀π sin(x) dx = 2",
      },
    ],
    seeAlso: ["trapz", "cumtrapz"],
  },

  // ── Polynomial & Interpolation ──────────────────────────────
  polyval: {
    name: "polyval",
    category: "Polynomial",
    synopsis: "polyval(p, x)",
    description:
      "Evaluates a polynomial with coefficients p at the point(s) x. Coefficients are in descending power order: p = [aₙ, aₙ₋₁, ..., a₁, a₀].",
    parameters: [
      {
        name: "p",
        type: "array",
        description: "Polynomial coefficients (highest degree first)",
      },
      {
        name: "x",
        type: "number | array",
        description: "Point(s) at which to evaluate",
      },
    ],
    returns: "Polynomial value(s)",
    examples: [
      {
        code: "polyval([1, -3, 2], 5)",
        result: "12",
        description: "x² - 3x + 2 at x=5",
      },
      {
        code: "polyval([1, 0, -1], [0, 1, 2])",
        result: "[-1, 0, 3]",
        description: "x² - 1 at multiple points",
      },
    ],
    seeAlso: ["roots", "poly", "polyfit"],
  },
  roots: {
    name: "roots",
    category: "Polynomial",
    synopsis: "roots(p)",
    description:
      "Finds the roots of a polynomial with coefficients p. Supports up to quadratic polynomials analytically; uses numerical methods for higher degrees.",
    parameters: [
      {
        name: "p",
        type: "array",
        description: "Polynomial coefficients (highest degree first)",
      },
    ],
    returns: "Array of roots (may include complex values)",
    examples: [
      {
        code: "roots([1, -5, 6])",
        result: "[3, 2]",
        description: "Roots of x² - 5x + 6",
      },
      {
        code: "roots([1, 0, 1])",
        result: "[i, -i]",
        description: "Roots of x² + 1 (complex)",
      },
    ],
    seeAlso: ["polyval", "poly", "polyfit"],
  },
  poly: {
    name: "poly",
    category: "Polynomial",
    synopsis: "poly(r)",
    description:
      "Constructs polynomial coefficients from its roots. Given roots r₁, r₂, ..., returns coefficients of (x - r₁)(x - r₂)...",
    parameters: [
      { name: "r", type: "array", description: "Array of polynomial roots" },
    ],
    returns: "Array of polynomial coefficients (highest degree first)",
    examples: [
      {
        code: "poly([3, 2])",
        result: "[1, -5, 6]",
        description: "(x-3)(x-2) = x² - 5x + 6",
      },
      {
        code: "poly([1, -1])",
        result: "[1, 0, -1]",
        description: "(x-1)(x+1) = x² - 1",
      },
    ],
    seeAlso: ["roots", "polyval"],
  },
  polyfit: {
    name: "polyfit",
    category: "Polynomial",
    synopsis: "polyfit(x, y, n)",
    description:
      "Fits a polynomial of degree n to the data points (x, y) using least-squares regression. Returns coefficients in descending power order.",
    parameters: [
      { name: "x", type: "array", description: "X data points" },
      { name: "y", type: "array", description: "Y data points" },
      { name: "n", type: "number", description: "Polynomial degree" },
    ],
    returns: "Array of fitted polynomial coefficients",
    examples: [
      {
        code: "polyfit([1,2,3,4], [1,4,9,16], 2)",
        result: "[1, 0, 0]",
        description: "Fit quadratic to perfect x² data",
      },
    ],
    seeAlso: ["polyval", "interp1"],
  },
  interp1: {
    name: "interp1",
    category: "Polynomial",
    synopsis: "interp1(x, y, xq)",
    description:
      "Performs 1D linear interpolation. Given data points (x, y), estimates y values at query points xq using piecewise linear interpolation.",
    parameters: [
      {
        name: "x",
        type: "array",
        description: "Known X coordinates (must be sorted)",
      },
      { name: "y", type: "array", description: "Known Y values" },
      { name: "xq", type: "number | array", description: "Query point(s)" },
    ],
    returns: "Interpolated value(s)",
    examples: [
      {
        code: "interp1([0, 1, 2], [0, 1, 4], 1.5)",
        result: "2.5",
        description: "Linear interpolation at x=1.5",
      },
      {
        code: "interp1([0, 1, 2], [0, 1, 4], [0.5, 1.5])",
        result: "[0.5, 2.5]",
        description: "Multiple query points",
      },
    ],
    seeAlso: ["polyfit", "polyval"],
  },

  // ── Signal Processing ───────────────────────────────────────
  conv: {
    name: "conv",
    category: "Signal Processing",
    synopsis: "conv(a, b)",
    description:
      "Computes the convolution of two arrays. The output length is len(a) + len(b) - 1. Used in signal processing and polynomial multiplication.",
    parameters: [
      { name: "a", type: "array", description: "First input array" },
      { name: "b", type: "array", description: "Second input array" },
    ],
    returns: "Convolution result array",
    examples: [
      {
        code: "conv([1, 2, 3], [1, 1])",
        result: "[1, 3, 5, 3]",
        description: "Simple convolution",
      },
      {
        code: "conv([1, 1], [1, 1])",
        result: "[1, 2, 1]",
        description: "Polynomial multiplication: (x+1)²",
      },
    ],
    seeAlso: ["fft", "ifft"],
  },
  fft: {
    name: "fft",
    category: "Signal Processing",
    synopsis: "fft(x)",
    description:
      "Computes the Discrete Fourier Transform (DFT) of array x. Returns an array of complex frequency-domain values. Use for spectral analysis of signals.",
    parameters: [
      { name: "x", type: "array", description: "Time-domain signal" },
    ],
    returns: "Array of complex frequency components",
    examples: [
      { code: "fft([1, 0, -1, 0])", description: "DFT of a simple signal" },
      {
        code: "x = linspace(0, 1, 256); fft(sin(2*pi*10*x))",
        description: "Spectrum of 10 Hz sine wave",
      },
    ],
    seeAlso: ["ifft", "conv"],
  },
  ifft: {
    name: "ifft",
    category: "Signal Processing",
    synopsis: "ifft(X)",
    description:
      "Computes the Inverse Discrete Fourier Transform, converting frequency-domain data back to time-domain.",
    parameters: [
      { name: "X", type: "array", description: "Frequency-domain data" },
    ],
    returns: "Time-domain signal array",
    examples: [
      {
        code: "ifft(fft([1, 2, 3, 4]))",
        result: "[1, 2, 3, 4]",
        description: "Round-trip FFT/IFFT",
      },
    ],
    seeAlso: ["fft", "conv"],
  },

  // ── Digital Filtering ───────────────────────────────────────
  filter: {
    name: "filter",
    category: "Signal Processing",
    synopsis: "filter(b, a, x)",
    description:
      "1-D digital filter (transposed direct-form II). Applies the rational transfer function defined by numerator coefficients b and denominator coefficients a to the input signal x. For FIR filters set a = 1.",
    parameters: [
      {
        name: "b",
        type: "array",
        description: "Numerator (feedforward) coefficients",
      },
      {
        name: "a",
        type: "array",
        description: "Denominator (feedback) coefficients",
      },
      { name: "x", type: "array", description: "Input signal" },
    ],
    returns: "Filtered signal array",
    examples: [
      {
        code: "filter([1, -1], 1, [1, 2, 4, 7])",
        result: "[1, 1, 2, 3]",
        description: "First-difference FIR filter",
      },
      {
        code: "filter(0.5, [1, -0.5], [1, 0, 0, 0])",
        result: "[0.5, 0.25, 0.125, 0.0625]",
        description: "IIR exponential decay",
      },
    ],
    seeAlso: ["ema", "conv", "fft"],
  },
  ema: {
    name: "ema",
    category: "Signal Processing",
    synopsis: "ema(x, alpha)",
    description:
      "Computes the exponential moving average of a signal. Each output sample is y(i) = y(i-1) + alpha * (x(i) - y(i-1)), with y(1) = x(1). Equivalent to filter(alpha, [1, -(1-alpha)], x).",
    parameters: [
      { name: "x", type: "array", description: "Input signal" },
      {
        name: "alpha",
        type: "number",
        description:
          "Smoothing factor in (0, 1]. Smaller values give more smoothing.",
      },
    ],
    returns: "Smoothed signal array",
    examples: [
      {
        code: "ema([1, 3, 5, 7, 9], 0.5)",
        result: "[1, 2, 3.5, 5.25, 7.125]",
        description: "Smooth a ramp with alpha = 0.5",
      },
    ],
    seeAlso: ["filter", "movmean", "conv"],
  },

  // ── Extended Statistics ─────────────────────────────────────
  corrcoef: {
    name: "corrcoef",
    category: "Statistics",
    synopsis: "corrcoef(x, y)",
    description:
      "Computes the Pearson correlation coefficient between two arrays. Returns a value between -1 (perfect negative correlation) and 1 (perfect positive correlation).",
    parameters: [
      { name: "x", type: "array", description: "First data array" },
      { name: "y", type: "array", description: "Second data array" },
    ],
    returns: "Correlation coefficient (-1 to 1)",
    examples: [
      {
        code: "corrcoef([1,2,3,4,5], [2,4,6,8,10])",
        result: "1",
        description: "Perfect positive correlation",
      },
      {
        code: "corrcoef([1,2,3,4,5], [5,4,3,2,1])",
        result: "-1",
        description: "Perfect negative correlation",
      },
    ],
    seeAlso: ["cov", "std", "mean"],
  },
  cov: {
    name: "cov",
    category: "Statistics",
    synopsis: "cov(x, y)",
    description:
      "Computes the covariance between two arrays. Measures how much two variables change together.",
    parameters: [
      { name: "x", type: "array", description: "First data array" },
      { name: "y", type: "array", description: "Second data array" },
    ],
    returns: "Covariance value",
    examples: [
      {
        code: "cov([1,2,3], [4,5,6])",
        description: "Covariance of two sequences",
      },
    ],
    seeAlso: ["corrcoef", "variance", "std"],
  },
  movmean: {
    name: "movmean",
    category: "Statistics",
    synopsis: "movmean(x, k)",
    description:
      "Computes the moving average (rolling mean) of array x with a window size of k. Useful for smoothing noisy data.",
    parameters: [
      { name: "x", type: "array", description: "Input data array" },
      { name: "k", type: "number", description: "Window size" },
    ],
    returns: "Array of moving averages",
    examples: [
      {
        code: "movmean([1,3,5,7,9], 3)",
        result: "[2, 3, 5, 7, 8]",
        description: "3-point moving average",
      },
      {
        code: "movmean(random(100), 10)",
        description: "Smooth noisy data with 10-point window",
      },
    ],
    seeAlso: ["mean", "corrcoef"],
  },
  histcounts: {
    name: "histcounts",
    category: "Statistics",
    synopsis: "histcounts(x, [nbins])",
    description:
      "Computes histogram bin counts without plotting. Returns the count of values in each bin.",
    parameters: [
      { name: "x", type: "array", description: "Data values" },
      {
        name: "nbins",
        type: "number",
        description: "Number of bins",
        optional: true,
        default: "10",
      },
    ],
    returns: "Array of bin counts",
    examples: [
      {
        code: "histcounts([1,2,2,3,3,3,4], 4)",
        description: "Count values in 4 bins",
      },
      {
        code: "histcounts(random(1000), 20)",
        description: "Distribution of random numbers",
      },
    ],
    seeAlso: ["histogram", "prctile"],
  },
  prctile: {
    name: "prctile",
    category: "Statistics",
    synopsis: "prctile(x, p)",
    description:
      "Computes the p-th percentile of array x. For example, prctile(x, 50) returns the median.",
    parameters: [
      { name: "x", type: "array", description: "Data array" },
      { name: "p", type: "number", description: "Percentile (0 to 100)" },
    ],
    returns: "Percentile value",
    examples: [
      {
        code: "prctile([1,2,3,4,5,6,7,8,9,10], 50)",
        result: "5.5",
        description: "Median (50th percentile)",
      },
      {
        code: "prctile([1,2,3,4,5,6,7,8,9,10], 25)",
        description: "First quartile",
      },
    ],
    seeAlso: ["median", "histcounts", "mean"],
  },

  // ── ODE Solver ──────────────────────────────────────────────
  ode45: {
    name: "ode45",
    category: "ODE",
    synopsis: "ode45(f, tspan, y0)",
    description:
      "Solves an ordinary differential equation dy/dt = f(t, y) using the Runge-Kutta 4th/5th order method (Dormand-Prince). Suitable for non-stiff problems.",
    parameters: [
      {
        name: "f",
        type: "function",
        description: "Function f(t, y) returning dy/dt",
      },
      {
        name: "tspan",
        type: "[t0, tf]",
        description: "Time span [start, end]",
      },
      {
        name: "y0",
        type: "number | array",
        description: "Initial condition(s)",
      },
    ],
    returns: "[t, y] — arrays of time points and solution values",
    examples: [
      {
        code: "[t, y] = ode45(fn(t,y) => -y, [0, 5], 1)",
        description: "Solve dy/dt = -y (exponential decay)",
      },
      {
        code: "[t, y] = ode45(fn(t,y) => [y[1], -y[0]], [0, 2*pi], [1, 0])",
        description: "Solve harmonic oscillator system",
      },
    ],
    seeAlso: ["odeplot", "odesolve"],
  },

  // ── Polar Plot (MATLAB-style) ───────────────────────────────
  polarplot: {
    name: "polar",
    category: "Plotting",
    synopsis: "polar(theta, r, [label])",
    description:
      "Creates a polar plot. Plots data points in polar coordinates where theta is the angle (in radians) and r is the radius.",
    parameters: [
      { name: "theta", type: "array", description: "Angles in radians" },
      { name: "r", type: "array", description: "Radii" },
      {
        name: "label",
        type: "string",
        description: "Series label",
        optional: true,
      },
    ],
    returns: "Plot object",
    examples: [
      {
        code: "theta = linspace(0, 2*pi, 100); polar(theta, cos(2*theta))",
        description: "Rose curve in polar coordinates",
      },
      {
        code: "theta = linspace(0, 4*pi, 200); polar(theta, theta)",
        description: "Archimedean spiral",
      },
    ],
    seeAlso: ["plot", "scatter"],
  },

  // ============================================================
  // SIGNAL PROCESSING TOOLBOX
  // ============================================================
  butter: {
    name: "butter",
    category: "Signal Processing",
    synopsis: "[b,a] = butter(n, Wn)\n[b,a] = butter(n, Wn, 'high')",
    description:
      "Design an Nth-order Butterworth digital filter and return numerator (b) and denominator (a) coefficients.",
    parameters: [
      { name: "n", type: "number", description: "Filter order" },
      {
        name: "Wn",
        type: "number",
        description: "Normalized cutoff frequency (0 to 1, where 1 = Nyquist)",
      },
      {
        name: "type",
        type: "string",
        description: "'high' for highpass",
        optional: true,
      },
    ],
    returns: "struct with fields b and a (filter coefficient vectors)",
    examples: [
      {
        code: "result = butter(4, 0.2)",
        description: "4th-order lowpass at 0.2×Nyquist",
      },
    ],
    seeAlso: ["fir1", "filter", "filtfilt", "freqz"],
  },
  fir1: {
    name: "fir1",
    category: "Signal Processing",
    synopsis: "b = fir1(n, Wn)",
    description:
      "Design an Nth-order FIR lowpass filter using windowed sinc with Hamming window.",
    parameters: [
      { name: "n", type: "number", description: "Filter order" },
      {
        name: "Wn",
        type: "number",
        description: "Normalized cutoff frequency",
      },
    ],
    returns: "FIR coefficient vector",
    examples: [
      { code: "b = fir1(20, 0.3)", description: "20th-order lowpass FIR" },
    ],
    seeAlso: ["butter", "filter"],
  },
  filtfilt: {
    name: "filtfilt",
    category: "Signal Processing",
    synopsis: "y = filtfilt(b, a, x)",
    description:
      "Zero-phase digital filtering—applies the filter forward and backward to eliminate phase distortion.",
    parameters: [
      { name: "b", type: "array", description: "Numerator coefficients" },
      { name: "a", type: "array", description: "Denominator coefficients" },
      { name: "x", type: "array", description: "Input signal" },
    ],
    returns: "Filtered signal (same length as x)",
    seeAlso: ["filter", "butter"],
  },
  freqz: {
    name: "freqz",
    category: "Signal Processing",
    synopsis: "result = freqz(b, a, N)",
    description: "Compute frequency response of a digital filter.",
    parameters: [
      { name: "b", type: "array", description: "Numerator coefficients" },
      { name: "a", type: "array", description: "Denominator coefficients" },
      {
        name: "N",
        type: "number",
        description: "Number of frequency points",
        optional: true,
        default: "512",
      },
    ],
    returns: "struct with fields w (frequency) and H (complex response)",
    seeAlso: ["butter", "fir1"],
  },
  findpeaks: {
    name: "findpeaks",
    category: "Signal Processing",
    synopsis: "result = findpeaks(x)",
    description:
      "Find local maxima (peaks) in a signal. Returns peak values and 1-based locations.",
    parameters: [{ name: "x", type: "array", description: "Input signal" }],
    returns: "struct with fields peaks and locs",
    examples: [
      {
        code: "result = findpeaks([0 1 0 2 0])",
        result: "peaks=[1,2], locs=[2,4]",
      },
    ],
    seeAlso: ["periodogram"],
  },
  periodogram: {
    name: "periodogram",
    category: "Signal Processing",
    synopsis: "Pxx = periodogram(x)",
    description:
      "Estimate power spectral density using the periodogram method.",
    parameters: [{ name: "x", type: "array", description: "Input signal" }],
    returns: "Power spectral density estimate",
    seeAlso: ["fft", "spectrogram"],
  },
  spectrogram: {
    name: "spectrogram",
    category: "Signal Processing",
    synopsis: "result = spectrogram(x, window, noverlap, nfft)",
    description: "Compute the short-time Fourier transform of signal x.",
    parameters: [
      { name: "x", type: "array", description: "Input signal" },
      {
        name: "window",
        type: "number",
        description: "Window length",
        optional: true,
      },
      {
        name: "noverlap",
        type: "number",
        description: "Overlap samples",
        optional: true,
      },
      {
        name: "nfft",
        type: "number",
        description: "FFT length",
        optional: true,
      },
    ],
    returns: "struct with fields S, f, t",
    seeAlso: ["fft", "periodogram"],
  },
  sgolayfilt: {
    name: "sgolayfilt",
    category: "Signal Processing",
    synopsis: "y = sgolayfilt(x, order, framelen)",
    description: "Savitzky-Golay finite impulse response smoothing filter.",
    parameters: [
      { name: "x", type: "array", description: "Input signal" },
      { name: "order", type: "number", description: "Polynomial order" },
      {
        name: "framelen",
        type: "number",
        description: "Frame length (odd integer)",
      },
    ],
    returns: "Smoothed signal",
    seeAlso: ["filtfilt", "movmean"],
  },
  resample: {
    name: "resample",
    category: "Signal Processing",
    synopsis: "y = resample(x, p, q)",
    description: "Resample signal x at p/q times the original rate.",
    parameters: [
      { name: "x", type: "array", description: "Input signal" },
      { name: "p", type: "number", description: "Upsample factor" },
      { name: "q", type: "number", description: "Downsample factor" },
    ],
    returns: "Resampled signal",
    seeAlso: ["upsample", "downsample"],
  },
  xcorr: {
    name: "xcorr",
    category: "Signal Processing",
    synopsis: "r = xcorr(x)\nr = xcorr(x, y)",
    description: "Cross-correlation (or autocorrelation if y is omitted).",
    parameters: [
      { name: "x", type: "array", description: "First signal" },
      {
        name: "y",
        type: "array",
        description: "Second signal",
        optional: true,
      },
    ],
    returns: "Cross-correlation sequence",
    seeAlso: ["conv", "fft"],
  },
  chirp: {
    name: "chirp",
    category: "Signal Processing",
    synopsis: "y = chirp(t, f0, t1, f1)",
    description: "Generate a swept-frequency cosine (chirp) signal.",
    parameters: [
      { name: "t", type: "array", description: "Time vector" },
      { name: "f0", type: "number", description: "Initial frequency (Hz)" },
      { name: "t1", type: "number", description: "End time" },
      { name: "f1", type: "number", description: "Frequency at end time (Hz)" },
    ],
    returns: "Chirp signal values",
    seeAlso: ["sinc", "hamming"],
  },
  sinc: {
    name: "sinc",
    category: "Signal Processing",
    synopsis: "y = sinc(x)",
    description: "Normalized sinc function: sin(pi*x)/(pi*x). sinc(0) = 1.",
    parameters: [{ name: "x", type: "number | array", description: "Input" }],
    returns: "Sinc values",
    seeAlso: ["chirp"],
  },
  hamming: {
    name: "hamming",
    category: "Signal Processing",
    synopsis: "w = hamming(N)",
    description: "Hamming window of length N.",
    parameters: [{ name: "N", type: "number", description: "Window length" }],
    returns: "Window vector",
    seeAlso: ["hanning", "blackman"],
  },
  hanning: {
    name: "hanning",
    category: "Signal Processing",
    synopsis: "w = hanning(N)",
    description: "Hanning (Hann) window of length N.",
    parameters: [{ name: "N", type: "number", description: "Window length" }],
    returns: "Window vector",
    seeAlso: ["hamming", "blackman"],
  },
  blackman: {
    name: "blackman",
    category: "Signal Processing",
    synopsis: "w = blackman(N)",
    description: "Blackman window of length N.",
    parameters: [{ name: "N", type: "number", description: "Window length" }],
    returns: "Window vector",
    seeAlso: ["hamming", "hanning"],
  },
  deconv: {
    name: "deconv",
    category: "Signal Processing",
    synopsis: "q = deconv(b, a)",
    description: "Deconvolution and polynomial division.",
    parameters: [
      { name: "b", type: "array", description: "Dividend polynomial" },
      { name: "a", type: "array", description: "Divisor polynomial" },
    ],
    returns: "Quotient polynomial coefficients",
    seeAlso: ["conv", "filter"],
  },

  // ============================================================
  // COMMUNICATIONS TOOLBOX
  // ============================================================
  qammod: {
    name: "qammod",
    category: "Communications",
    synopsis: "y = qammod(x, M)",
    description:
      "Quadrature amplitude modulation. Maps integer symbols (0 to M-1) to complex constellation points.",
    parameters: [
      {
        name: "x",
        type: "array",
        description: "Integer symbol indices (0 to M-1)",
      },
      {
        name: "M",
        type: "number",
        description: "Modulation order (4, 16, 64, ...)",
      },
    ],
    returns: "Complex symbol array",
    examples: [{ code: "y = qammod([0 1 2 3], 4)" }],
    seeAlso: ["qamdemod", "pskmod", "awgn"],
  },
  qamdemod: {
    name: "qamdemod",
    category: "Communications",
    synopsis: "x = qamdemod(y, M)",
    description:
      "QAM demodulation. Maps complex symbols back to integer indices.",
    parameters: [
      { name: "y", type: "array", description: "Complex received symbols" },
      { name: "M", type: "number", description: "Modulation order" },
    ],
    returns: "Demodulated integer symbols",
    seeAlso: ["qammod", "pskdemod"],
  },
  pskmod: {
    name: "pskmod",
    category: "Communications",
    synopsis: "y = pskmod(x, M)",
    description: "Phase shift keying modulation.",
    parameters: [
      {
        name: "x",
        type: "array",
        description: "Integer symbol indices (0 to M-1)",
      },
      {
        name: "M",
        type: "number",
        description: "Modulation order (2=BPSK, 4=QPSK, 8=8PSK)",
      },
    ],
    returns: "Complex unit-amplitude symbols",
    seeAlso: ["pskdemod", "qammod"],
  },
  pskdemod: {
    name: "pskdemod",
    category: "Communications",
    synopsis: "x = pskdemod(y, M)",
    description: "PSK demodulation.",
    parameters: [
      { name: "y", type: "array", description: "Complex received symbols" },
      { name: "M", type: "number", description: "Modulation order" },
    ],
    returns: "Demodulated integer symbols",
    seeAlso: ["pskmod", "qamdemod"],
  },
  awgn: {
    name: "awgn",
    category: "Communications",
    synopsis: "y = awgn(x, snr)",
    description: "Add white Gaussian noise at the specified SNR (dB).",
    parameters: [
      { name: "x", type: "array", description: "Input signal" },
      {
        name: "snr",
        type: "number",
        description: "Signal-to-noise ratio in dB",
      },
    ],
    returns: "Noisy signal",
    seeAlso: ["qammod", "berawgn"],
  },
  biterr: {
    name: "biterr",
    category: "Communications",
    synopsis: "errors = biterr(a, b)",
    description: "Count the number of bit errors between two integer arrays.",
    parameters: [
      { name: "a", type: "array", description: "First integer array" },
      { name: "b", type: "array", description: "Second integer array" },
    ],
    returns: "Number of bit errors",
    seeAlso: ["berawgn"],
  },
  berawgn: {
    name: "berawgn",
    category: "Communications",
    synopsis: "ber = berawgn(EbNo, modtype, M)",
    description:
      "Compute theoretical BER for a modulation type in AWGN channel.",
    parameters: [
      { name: "EbNo", type: "number", description: "Eb/No in dB" },
      {
        name: "modtype",
        type: "string",
        description: "Modulation type: 'psk' or 'qam'",
      },
      { name: "M", type: "number", description: "Modulation order" },
    ],
    returns: "Bit error rate scalar",
    seeAlso: ["biterr", "awgn"],
  },
  rcosdesign: {
    name: "rcosdesign",
    category: "Communications",
    synopsis: "h = rcosdesign(beta, span, sps)",
    description: "Design a raised cosine FIR filter for pulse shaping.",
    parameters: [
      { name: "beta", type: "number", description: "Roll-off factor (0 to 1)" },
      { name: "span", type: "number", description: "Filter span in symbols" },
      { name: "sps", type: "number", description: "Samples per symbol" },
    ],
    returns: "FIR coefficient vector",
    seeAlso: ["filter", "conv"],
  },

  // ============================================================
  // OPTIMIZATION TOOLBOX
  // ============================================================
  fminbnd: {
    name: "fminbnd",
    category: "Optimization",
    synopsis: "x = fminbnd(fun, a, b)",
    description:
      "Find the minimum of a scalar function on [a, b] using golden section search.",
    parameters: [
      {
        name: "fun",
        type: "function_handle",
        description: "Objective function @(x) f(x)",
      },
      { name: "a", type: "number", description: "Lower bound" },
      { name: "b", type: "number", description: "Upper bound" },
    ],
    returns: "Minimizer x",
    examples: [{ code: "x = fminbnd(@(x) (x-2)^2, 0, 4)", result: "≈ 2" }],
    seeAlso: ["fminsearch", "fzero"],
  },
  fminsearch: {
    name: "fminsearch",
    category: "Optimization",
    synopsis: "x = fminsearch(fun, x0)",
    description: "Unconstrained minimization via Nelder-Mead simplex.",
    parameters: [
      {
        name: "fun",
        type: "function_handle",
        description: "Objective function",
      },
      { name: "x0", type: "number | array", description: "Initial guess" },
    ],
    returns: "Minimizer x",
    examples: [{ code: "x = fminsearch(@(x) (x-3)^2, 0)", result: "≈ 3" }],
    seeAlso: ["fminbnd", "lsqnonlin"],
  },
  fzero: {
    name: "fzero",
    category: "Optimization",
    synopsis: "x = fzero(fun, x0)",
    description: "Find a root of a scalar function using Newton's method.",
    parameters: [
      {
        name: "fun",
        type: "function_handle",
        description: "Function to find zero of",
      },
      { name: "x0", type: "number", description: "Initial guess" },
    ],
    returns: "Root x where fun(x) ≈ 0",
    examples: [{ code: "x = fzero(@(x) x^2-4, 1)", result: "≈ 2" }],
    seeAlso: ["fminbnd"],
  },
  linprog: {
    name: "linprog",
    category: "Optimization",
    synopsis: "x = linprog(f, A, b)",
    description: "Linear programming: minimize f'*x subject to A*x ≤ b, x ≥ 0.",
    parameters: [
      { name: "f", type: "array", description: "Objective coefficients" },
      {
        name: "A",
        type: "matrix",
        description: "Inequality constraint matrix",
      },
      { name: "b", type: "array", description: "Inequality constraint vector" },
    ],
    returns: "Optimal solution vector",
    seeAlso: ["quadprog"],
  },
  lsqnonlin: {
    name: "lsqnonlin",
    category: "Optimization",
    synopsis: "x = lsqnonlin(fun, x0)",
    description:
      "Nonlinear least-squares minimization using Levenberg-Marquardt.",
    parameters: [
      {
        name: "fun",
        type: "function_handle",
        description: "Residual function",
      },
      {
        name: "x0",
        type: "number | array",
        description: "Initial parameter guess",
      },
    ],
    returns: "Optimal parameters",
    seeAlso: ["fminsearch"],
  },
  quadprog: {
    name: "quadprog",
    category: "Optimization",
    synopsis: "x = quadprog(H, f)",
    description: "Quadratic programming: minimize 0.5*x'*H*x + f'*x.",
    parameters: [
      { name: "H", type: "matrix", description: "Quadratic cost matrix" },
      { name: "f", type: "array", description: "Linear cost vector" },
    ],
    returns: "Optimal solution",
    seeAlso: ["linprog"],
  },

  // ============================================================
  // STATISTICS AND MACHINE LEARNING TOOLBOX
  // ============================================================
  fitlm: {
    name: "fitlm",
    category: "Statistics",
    synopsis: "mdl = fitlm(X, y)",
    description: "Fit a simple linear regression model y = b0 + b1*x.",
    parameters: [
      { name: "X", type: "array", description: "Predictor data" },
      { name: "y", type: "array", description: "Response data" },
    ],
    returns: "struct with intercept, slope, rSquared, coefficients",
    examples: [
      {
        code: "mdl = fitlm([1 2 3 4], [2 4 6 8])",
        result: "slope=2, intercept=0",
      },
    ],
    seeAlso: ["polyfit", "corrcoef"],
  },
  kmeans: {
    name: "kmeans",
    category: "Statistics",
    synopsis: "result = kmeans(X, k)",
    description: "K-means clustering. Partition data into k clusters.",
    parameters: [
      { name: "X", type: "matrix | array", description: "Data" },
      { name: "k", type: "number", description: "Number of clusters" },
    ],
    returns: "struct with labels and centroids",
    examples: [{ code: "result = kmeans([1;2;10;11], 2)" }],
    seeAlso: ["pca"],
  },
  pca: {
    name: "pca",
    category: "Statistics",
    synopsis: "result = pca(X)",
    description: "Principal component analysis.",
    parameters: [
      {
        name: "X",
        type: "matrix",
        description: "Data matrix (rows=observations)",
      },
    ],
    returns: "struct with coeff, score, latent",
    seeAlso: ["kmeans", "corrcoef"],
  },
  ttest: {
    name: "ttest",
    category: "Statistics",
    synopsis: "result = ttest(x)",
    description: "One-sample t-test (null hypothesis: mean = 0).",
    parameters: [{ name: "x", type: "array", description: "Sample data" }],
    returns: "struct with h, p, tstat, mean",
    seeAlso: ["mean", "std"],
  },

  // ============================================================
  // LINEAR ALGEBRA (ADVANCED)
  // ============================================================
  svd: {
    name: "svd",
    category: "Linear Algebra",
    synopsis: "s = svd(A)",
    description:
      "Singular value decomposition. Returns singular values in descending order.",
    parameters: [{ name: "A", type: "matrix", description: "Input matrix" }],
    returns: "Array of singular values",
    examples: [{ code: "svd([3 0; 0 4])", result: "[4, 3]" }],
    seeAlso: ["eig", "rank", "pinv"],
  },
  qr: {
    name: "qr",
    category: "Linear Algebra",
    synopsis: "result = qr(A)",
    description:
      "QR factorization (Gram-Schmidt). A = Q*R with Q orthogonal and R upper triangular.",
    parameters: [{ name: "A", type: "matrix", description: "Input matrix" }],
    returns: "struct with Q and R",
    seeAlso: ["lu", "svd"],
  },
  rank: {
    name: "rank",
    category: "Linear Algebra",
    synopsis: "r = rank(A)",
    description: "Numerical rank via Gaussian elimination with pivoting.",
    parameters: [{ name: "A", type: "matrix", description: "Input matrix" }],
    returns: "Integer rank",
    examples: [{ code: "rank([1 2; 2 4])", result: "1" }],
    seeAlso: ["svd", "det"],
  },
  pinv: {
    name: "pinv",
    category: "Linear Algebra",
    synopsis: "B = pinv(A)",
    description: "Moore-Penrose pseudoinverse.",
    parameters: [{ name: "A", type: "matrix", description: "Input matrix" }],
    returns: "Pseudoinverse matrix",
    seeAlso: ["inv", "rank"],
  },
  cond: {
    name: "cond",
    category: "Linear Algebra",
    synopsis: "c = cond(A)",
    description:
      "Matrix condition number (ratio of largest to smallest singular value).",
    parameters: [{ name: "A", type: "matrix", description: "Input matrix" }],
    returns: "Condition number",
    seeAlso: ["svd", "rank"],
  },
  tril: {
    name: "tril",
    category: "Linear Algebra",
    synopsis: "L = tril(A)",
    description: "Extract lower triangular part of a matrix.",
    parameters: [{ name: "A", type: "matrix", description: "Input matrix" }],
    returns: "Lower triangular matrix",
    seeAlso: ["triu", "diag"],
  },
  triu: {
    name: "triu",
    category: "Linear Algebra",
    synopsis: "U = triu(A)",
    description: "Extract upper triangular part of a matrix.",
    parameters: [{ name: "A", type: "matrix", description: "Input matrix" }],
    returns: "Upper triangular matrix",
    seeAlso: ["tril", "diag"],
  },
  kron: {
    name: "kron",
    category: "Linear Algebra",
    synopsis: "K = kron(A, B)",
    description: "Kronecker tensor product of two matrices.",
    parameters: [
      { name: "A", type: "matrix", description: "First matrix" },
      { name: "B", type: "matrix", description: "Second matrix" },
    ],
    returns: "Kronecker product matrix",
    seeAlso: ["eye", "diag"],
  },

  // ============================================================
  // CONTROL SYSTEMS TOOLBOX
  // ============================================================
  tf: {
    name: "tf",
    category: "Control Systems",
    synopsis: "sys = tf(num, den)",
    description:
      "Create a transfer function model from numerator and denominator polynomial coefficients.",
    parameters: [
      { name: "num", type: "array", description: "Numerator coefficients" },
      { name: "den", type: "array", description: "Denominator coefficients" },
    ],
    returns: "Transfer function model",
    examples: [{ code: "sys = tf([1], [1 2 1])", description: "1/(s²+2s+1)" }],
    seeAlso: ["ss", "zpk", "step", "bode"],
  },
  ss: {
    name: "ss",
    category: "Control Systems",
    synopsis: "sys = ss(A, B, C, D)",
    description: "Create a state-space model.",
    parameters: [
      { name: "A", type: "matrix", description: "State matrix" },
      { name: "B", type: "matrix", description: "Input matrix" },
      { name: "C", type: "matrix", description: "Output matrix" },
      { name: "D", type: "matrix", description: "Feedthrough matrix" },
    ],
    returns: "State-space model",
    seeAlso: ["tf", "zpk"],
  },
  zpk: {
    name: "zpk",
    category: "Control Systems",
    synopsis: "sys = zpk(z, p, k)",
    description: "Create a zero-pole-gain model.",
    parameters: [
      { name: "z", type: "array", description: "Zeros" },
      { name: "p", type: "array", description: "Poles" },
      { name: "k", type: "number", description: "Gain" },
    ],
    returns: "Zero-pole-gain model",
    seeAlso: ["tf", "ss"],
  },
  step: {
    name: "step",
    category: "Control Systems",
    synopsis: "result = step(sys)\nresult = step(sys, tFinal)",
    description: "Compute step response of a linear system.",
    parameters: [
      { name: "sys", type: "tf", description: "Transfer function model" },
      {
        name: "tFinal",
        type: "number",
        description: "End time",
        optional: true,
      },
    ],
    returns: "struct with t and y arrays",
    seeAlso: ["impulse", "bode", "tf"],
  },
  impulse: {
    name: "impulse",
    category: "Control Systems",
    synopsis: "result = impulse(sys)",
    description: "Compute impulse response of a linear system.",
    parameters: [
      { name: "sys", type: "tf", description: "Transfer function model" },
    ],
    returns: "struct with t and y arrays",
    seeAlso: ["step", "bode"],
  },
  bode: {
    name: "bode",
    category: "Control Systems",
    synopsis: "result = bode(sys)",
    description: "Compute Bode magnitude and phase of a frequency response.",
    parameters: [
      { name: "sys", type: "tf", description: "Transfer function model" },
    ],
    returns: "struct with w, mag (dB), phase (degrees)",
    seeAlso: ["margin", "step"],
  },
  margin: {
    name: "margin",
    category: "Control Systems",
    synopsis: "result = margin(sys)",
    description:
      "Compute gain margin, phase margin, and crossover frequencies.",
    parameters: [
      { name: "sys", type: "tf", description: "Transfer function model" },
    ],
    returns: "struct with gainMargin, phaseMargin, gmFreq, pmFreq",
    seeAlso: ["bode", "feedback"],
  },
  feedback: {
    name: "feedback",
    category: "Control Systems",
    synopsis: "sys_cl = feedback(G, H)",
    description:
      "Closed-loop transfer function G/(1+G*H) for negative feedback.",
    parameters: [
      { name: "G", type: "tf", description: "Forward path" },
      {
        name: "H",
        type: "number | tf",
        description: "Feedback path (1 for unity)",
      },
    ],
    returns: "Closed-loop transfer function",
    seeAlso: ["tf", "margin"],
  },
  c2d: {
    name: "c2d",
    category: "Control Systems",
    synopsis: "sys_d = c2d(sys, Ts)",
    description: "Convert continuous-time system to discrete-time.",
    parameters: [
      { name: "sys", type: "tf", description: "Continuous system" },
      { name: "Ts", type: "number", description: "Sampling period" },
    ],
    returns: "Discrete-time transfer function",
    seeAlso: ["tf"],
  },

  // ============================================================
  // ADDITIONAL CORE FUNCTIONS
  // ============================================================
  cumsum: {
    name: "cumsum",
    category: "Array Operations",
    synopsis: "y = cumsum(x)",
    description: "Cumulative sum of array elements.",
    parameters: [{ name: "x", type: "array", description: "Input" }],
    returns: "Cumulative sum array",
    seeAlso: ["sum", "cumprod"],
  },
  cumprod: {
    name: "cumprod",
    category: "Array Operations",
    synopsis: "y = cumprod(x)",
    description: "Cumulative product of array elements.",
    parameters: [{ name: "x", type: "array", description: "Input" }],
    returns: "Cumulative product array",
    seeAlso: ["prod", "cumsum"],
  },
  repmat: {
    name: "repmat",
    category: "Array Operations",
    synopsis: "B = repmat(A, m, n)",
    description: "Replicate and tile a matrix.",
    parameters: [
      { name: "A", type: "number | array | matrix", description: "Input" },
      { name: "m", type: "number", description: "Row repetitions" },
      {
        name: "n",
        type: "number",
        description: "Column repetitions",
        optional: true,
      },
    ],
    returns: "Tiled matrix",
    seeAlso: ["ones", "zeros"],
  },
  integral: {
    name: "integral",
    category: "Calculus",
    synopsis: "q = integral(fun, a, b)",
    description: "Numerical integration using adaptive Simpson's rule.",
    parameters: [
      { name: "fun", type: "function_handle", description: "Integrand" },
      { name: "a", type: "number", description: "Lower limit" },
      { name: "b", type: "number", description: "Upper limit" },
    ],
    returns: "Definite integral value",
    examples: [{ code: "integral(@(x) x^2, 0, 1)", result: "≈ 0.3333" }],
    seeAlso: ["trapz", "quad"],
  },

  // ============================================================
  // TOOLBOX MANAGEMENT
  // ============================================================
  loadToolbox: {
    name: "loadToolbox",
    category: "System",
    synopsis: "loadToolbox('toolbox-id')",
    description:
      "Load a toolbox's helper functions into the current environment. Built-in functions are always available; this loads additional helper scripts.",
    parameters: [
      {
        name: "id",
        type: "string",
        description: "Toolbox ID (e.g. 'signal-processing-toolbox')",
      },
    ],
    returns: "Confirmation message",
    examples: [
      {
        code: "loadToolbox('signal-processing-toolbox')",
        description: "Load signal processing helpers",
      },
      { code: "listToolboxes()", description: "Show available toolboxes" },
    ],
    seeAlso: ["listToolboxes", "toolboxHelp"],
  },
  listToolboxes: {
    name: "listToolboxes",
    category: "System",
    synopsis: "listToolboxes()",
    description:
      "List all available toolbox packages with their IDs and function counts.",
    returns: "Formatted list of toolboxes",
    seeAlso: ["loadToolbox", "toolboxHelp"],
  },
  toolboxHelp: {
    name: "toolboxHelp",
    category: "System",
    synopsis: "toolboxHelp('toolbox-id')",
    description: "Show details about a toolbox including its functions.",
    parameters: [{ name: "id", type: "string", description: "Toolbox ID" }],
    returns: "Toolbox description and function list",
    seeAlso: ["loadToolbox", "listToolboxes"],
  },

  // ============================================================
  // DATA CONTAINERS
  // ============================================================
  string: {
    name: "string",
    category: "Data Containers",
    synopsis: "string(x)",
    description: "Convert a value to its string representation.",
    parameters: [{ name: "x", type: "any", description: "Value to convert" }],
    returns: "String representation",
    examples: [
      { code: "string(42)", result: '"42"' },
      { code: "string([1 2 3])", result: '"1 2 3"' },
    ],
    seeAlso: ["num2str", "str2num"],
  },
  str2double: {
    name: "str2double",
    category: "Data Containers",
    synopsis: "str2double(s)",
    description:
      "Convert string to double-precision number. Returns NaN for invalid input.",
    parameters: [
      { name: "s", type: "string", description: "String to convert" },
    ],
    returns: "Number or NaN",
    examples: [{ code: "str2double('3.14')", result: "3.14" }],
    seeAlso: ["str2num", "num2str"],
  },
  datetime: {
    name: "datetime",
    category: "Data Containers",
    synopsis: "datetime(y, m, d)\ndatetime(y, m, d, h, mi, s)",
    description:
      "Create a date-time representation. With no arguments returns current date/time.",
    parameters: [
      { name: "y", type: "number", description: "Year" },
      { name: "m", type: "number", description: "Month (1-12)" },
      { name: "d", type: "number", description: "Day" },
      { name: "h", type: "number", description: "Hour", optional: true },
      { name: "mi", type: "number", description: "Minute", optional: true },
      { name: "s", type: "number", description: "Second", optional: true },
    ],
    returns: "Date-time string",
    examples: [
      { code: "datetime(2026, 3, 28)", result: '"28-Mar-2026"' },
      {
        code: "datetime(2026, 3, 28, 14, 30, 0)",
        result: '"28-Mar-2026 14:30:00"',
      },
    ],
    seeAlso: ["hours", "minutes", "now", "datestr"],
  },
  hours: {
    name: "hours",
    category: "Data Containers",
    synopsis: "hours(n)",
    description: "Create a duration value in hours.",
    parameters: [{ name: "n", type: "number", description: "Number of hours" }],
    returns: "Duration string",
    examples: [{ code: "hours(2)", result: '"2 hr"' }],
    seeAlso: ["minutes", "seconds", "days", "datetime"],
  },
  minutes: {
    name: "minutes",
    category: "Data Containers",
    synopsis: "minutes(n)",
    description: "Create a duration value in minutes.",
    parameters: [
      { name: "n", type: "number", description: "Number of minutes" },
    ],
    returns: "Duration string",
    examples: [{ code: "minutes(30)", result: '"30 min"' }],
    seeAlso: ["hours", "seconds", "days"],
  },
  seconds: {
    name: "seconds",
    category: "Data Containers",
    synopsis: "seconds(n)",
    description: "Create a duration value in seconds.",
    parameters: [
      { name: "n", type: "number", description: "Number of seconds" },
    ],
    returns: "Duration string",
    examples: [{ code: "seconds(90)", result: '"90 sec"' }],
    seeAlso: ["hours", "minutes", "days"],
  },
  days: {
    name: "days",
    category: "Data Containers",
    synopsis: "days(n)",
    description: "Create a duration value in days.",
    parameters: [{ name: "n", type: "number", description: "Number of days" }],
    returns: "Duration string",
    examples: [{ code: "days(7)", result: '"7 days"' }],
    seeAlso: ["hours", "minutes", "seconds"],
  },
  now: {
    name: "now",
    category: "Data Containers",
    synopsis: "now",
    description: "Return current date/time as serial date number.",
    returns: "Serial date number",
    seeAlso: ["datetime", "datestr"],
  },
  datestr: {
    name: "datestr",
    category: "Data Containers",
    synopsis: "datestr(d)",
    description: "Convert serial date number to date string.",
    parameters: [
      { name: "d", type: "number", description: "Serial date number" },
    ],
    returns: "Date string",
    seeAlso: ["datetime", "now"],
  },
  table: {
    name: "table",
    category: "Data Containers",
    synopsis: "table(a, b, ...)",
    description:
      "Create a table from column vectors. Each argument becomes a column (Var1, Var2, etc.).",
    parameters: [
      { name: "a, b, ...", type: "array", description: "Column data" },
    ],
    returns: "Formatted table string",
    examples: [
      { code: "table([1;2], [3;4])", result: "2-row, 2-column table" },
    ],
    seeAlso: ["categorical"],
  },
  categorical: {
    name: "categorical",
    category: "Data Containers",
    synopsis: "categorical(arr)",
    description: "Create a categorical array from values.",
    parameters: [
      { name: "arr", type: "array", description: "Values to categorize" },
    ],
    returns: "Categorical representation",
    seeAlso: ["table"],
  },

  // ============================================================
  // ADDITIONAL CORE FUNCTIONS
  // ============================================================
  fprintf: {
    name: "fprintf",
    category: "I/O",
    synopsis: "fprintf(fmt, ...)",
    description:
      "Format data and write to string. Supports %d, %f, %e, %g, %s, %c format specifiers.",
    parameters: [
      { name: "fmt", type: "string", description: "Format string" },
      { name: "...", type: "any", description: "Values to format" },
    ],
    returns: "Formatted string",
    examples: [
      { code: "fprintf('%d + %d = %d', 2, 3, 5)", result: '"2 + 3 = 5"' },
      { code: "fprintf('pi = %.4f', pi)", result: '"pi = 3.1416"' },
    ],
    seeAlso: ["sprintf", "disp"],
  },
  error: {
    name: "error",
    category: "System",
    synopsis: "error(msg)",
    description: "Throw an error with the specified message.",
    parameters: [{ name: "msg", type: "string", description: "Error message" }],
    seeAlso: ["warning", "disp"],
  },
  warning: {
    name: "warning",
    category: "System",
    synopsis: "warning(msg)",
    description: "Display a warning message.",
    parameters: [
      { name: "msg", type: "string", description: "Warning message" },
    ],
    returns: "Warning string",
    seeAlso: ["error", "disp"],
  },
  conv2: {
    name: "conv2",
    category: "Signal Processing",
    synopsis: "C = conv2(A, B)",
    description: "Two-dimensional convolution of matrices A and B.",
    parameters: [
      { name: "A", type: "matrix", description: "First input matrix" },
      { name: "B", type: "matrix", description: "Second input matrix" },
    ],
    returns: "Convolution result matrix",
    seeAlso: ["conv", "imfilter"],
  },
  fft2: {
    name: "fft2",
    category: "Signal Processing",
    synopsis: "Y = fft2(X)",
    description: "Two-dimensional discrete Fourier transform.",
    parameters: [{ name: "X", type: "matrix", description: "Input matrix" }],
    returns: "2-D DFT result",
    seeAlso: ["ifft2", "fft"],
  },
  ifft2: {
    name: "ifft2",
    category: "Signal Processing",
    synopsis: "Y = ifft2(X)",
    description: "Two-dimensional inverse discrete Fourier transform.",
    parameters: [{ name: "X", type: "matrix", description: "Input matrix" }],
    returns: "2-D inverse DFT result",
    seeAlso: ["fft2", "ifft"],
  },
  blkdiag: {
    name: "blkdiag",
    category: "Matrix Operations",
    synopsis: "blkdiag(A, B, ...)",
    description: "Create a block diagonal matrix from input matrices.",
    parameters: [
      {
        name: "A, B, ...",
        type: "matrix | number",
        description: "Block matrices",
      },
    ],
    returns: "Block diagonal matrix",
    examples: [
      { code: "blkdiag([1 0; 0 1], [2])", result: "[1 0 0; 0 1 0; 0 0 2]" },
    ],
    seeAlso: ["diag", "eye"],
  },
  expm: {
    name: "expm",
    category: "Matrix Operations",
    synopsis: "expm(A)",
    description:
      "Compute the matrix exponential of A using Padé approximation with scaling and squaring.",
    parameters: [{ name: "A", type: "matrix", description: "Square matrix" }],
    returns: "Matrix exponential",
    seeAlso: ["eig", "inv"],
  },
  chol: {
    name: "chol",
    category: "Matrix Operations",
    synopsis: "R = chol(A)",
    description:
      "Cholesky factorization. Returns upper triangular R such that R'*R = A.",
    parameters: [
      {
        name: "A",
        type: "matrix",
        description: "Symmetric positive definite matrix",
      },
    ],
    returns: "Upper triangular Cholesky factor",
    seeAlso: ["lu", "qr", "eig"],
  },
  schur: {
    name: "schur",
    category: "Matrix Operations",
    synopsis: "T = schur(A)",
    description:
      "Schur decomposition. Returns quasi-upper triangular Schur form.",
    parameters: [{ name: "A", type: "matrix", description: "Square matrix" }],
    returns: "Schur form matrix",
    seeAlso: ["eig", "svd"],
  },
  nnz: {
    name: "nnz",
    category: "Array Operations",
    synopsis: "nnz(x)",
    description: "Number of nonzero elements in array.",
    parameters: [
      { name: "x", type: "array | matrix", description: "Input array" },
    ],
    returns: "Count of nonzero elements",
    examples: [{ code: "nnz([0 1 2 0 3])", result: "3" }],
    seeAlso: ["find", "any", "sparse"],
  },
  repelem: {
    name: "repelem",
    category: "Array Operations",
    synopsis: "repelem(x, n)",
    description: "Repeat elements of array n times each.",
    parameters: [
      { name: "x", type: "array", description: "Input array" },
      { name: "n", type: "number", description: "Repetition count" },
    ],
    returns: "Array with repeated elements",
    examples: [{ code: "repelem([1 2 3], 2)", result: "[1 1 2 2 3 3]" }],
    seeAlso: ["repmat"],
  },
  issorted: {
    name: "issorted",
    category: "Array Operations",
    synopsis: "issorted(x)",
    description: "Check if array elements are in sorted order.",
    parameters: [{ name: "x", type: "array", description: "Input array" }],
    returns: "1 if sorted, 0 otherwise",
    examples: [{ code: "issorted([1 2 3])", result: "1" }],
    seeAlso: ["sort", "unique"],
  },
  setdiff: {
    name: "setdiff",
    category: "Array Operations",
    synopsis: "setdiff(a, b)",
    description: "Set difference: elements in a that are not in b.",
    parameters: [
      { name: "a", type: "array", description: "First array" },
      { name: "b", type: "array", description: "Second array" },
    ],
    returns: "Sorted difference array",
    examples: [{ code: "setdiff([1 2 3 4], [2 4])", result: "[1, 3]" }],
    seeAlso: ["union", "intersect", "ismember"],
  },
  union: {
    name: "union",
    category: "Array Operations",
    synopsis: "union(a, b)",
    description: "Set union of two arrays.",
    parameters: [
      { name: "a", type: "array", description: "First array" },
      { name: "b", type: "array", description: "Second array" },
    ],
    returns: "Sorted union array",
    examples: [{ code: "union([1 2], [2 3])", result: "[1, 2, 3]" }],
    seeAlso: ["intersect", "setdiff"],
  },
  intersect: {
    name: "intersect",
    category: "Array Operations",
    synopsis: "intersect(a, b)",
    description: "Set intersection of two arrays.",
    parameters: [
      { name: "a", type: "array", description: "First array" },
      { name: "b", type: "array", description: "Second array" },
    ],
    returns: "Sorted intersection array",
    examples: [{ code: "intersect([1 2 3], [2 3 4])", result: "[2, 3]" }],
    seeAlso: ["union", "setdiff"],
  },
  ismember: {
    name: "ismember",
    category: "Array Operations",
    synopsis: "ismember(a, b)",
    description: "Test which elements of a are in b.",
    parameters: [
      { name: "a", type: "number | array", description: "Elements to test" },
      { name: "b", type: "array", description: "Set to test against" },
    ],
    returns: "Logical array (1 if member, 0 if not)",
    examples: [{ code: "ismember([1 2 3], [2 4])", result: "[0, 1, 0]" }],
    seeAlso: ["intersect", "setdiff", "find"],
  },
  sub2ind: {
    name: "sub2ind",
    category: "Array Operations",
    synopsis: "sub2ind(sz, row, col)",
    description: "Convert subscripts to linear indices.",
    parameters: [
      { name: "sz", type: "array", description: "Array size [m, n]" },
      { name: "row", type: "number", description: "Row subscript" },
      { name: "col", type: "number", description: "Column subscript" },
    ],
    returns: "Linear index",
    seeAlso: ["ind2sub", "size"],
  },
  ind2sub: {
    name: "ind2sub",
    category: "Array Operations",
    synopsis: "ind2sub(sz, ind)",
    description: "Convert linear index to subscripts.",
    parameters: [
      { name: "sz", type: "array", description: "Array size [m, n]" },
      { name: "ind", type: "number", description: "Linear index" },
    ],
    returns: "[row, col] subscripts",
    seeAlso: ["sub2ind", "size"],
  },

  // ============================================================
  // IMAGE PROCESSING
  // ============================================================
  imread: {
    name: "imread",
    category: "Image Processing",
    synopsis: "I = imread(filename)",
    description:
      "Read an image file. In browser context, returns a test gradient image matrix.",
    parameters: [
      { name: "filename", type: "string", description: "Image file path" },
    ],
    returns: "Image matrix (grayscale intensity values)",
    seeAlso: ["imwrite", "imshow", "rgb2gray"],
  },
  imwrite: {
    name: "imwrite",
    category: "Image Processing",
    synopsis: "imwrite(I, filename)",
    description: "Write image matrix to file (stub in browser context).",
    parameters: [
      { name: "I", type: "matrix", description: "Image data" },
      { name: "filename", type: "string", description: "Output file path" },
    ],
    seeAlso: ["imread", "imshow"],
  },
  imshow: {
    name: "imshow",
    category: "Image Processing",
    synopsis: "imshow(I)",
    description: "Display an image matrix as a heatmap visualization.",
    parameters: [{ name: "I", type: "matrix", description: "Image matrix" }],
    returns: "Heatmap plot object",
    seeAlso: ["imread", "imfilter"],
  },
  rgb2gray: {
    name: "rgb2gray",
    category: "Image Processing",
    synopsis: "J = rgb2gray(I)",
    description:
      "Convert RGB image to grayscale using luminance weights (0.2989*R + 0.5870*G + 0.1140*B).",
    parameters: [
      { name: "I", type: "matrix", description: "RGB image matrix (M×3N)" },
    ],
    returns: "Grayscale image matrix",
    seeAlso: ["imread", "imshow"],
  },
  imresize: {
    name: "imresize",
    category: "Image Processing",
    synopsis: "J = imresize(I, scale)",
    description:
      "Resize image by scale factor using nearest-neighbor interpolation.",
    parameters: [
      { name: "I", type: "matrix", description: "Input image" },
      {
        name: "scale",
        type: "number",
        description: "Scale factor (e.g. 0.5 = half size)",
      },
    ],
    returns: "Resized image matrix",
    seeAlso: ["imread", "imfilter"],
  },
  imfilter: {
    name: "imfilter",
    category: "Image Processing",
    synopsis: "J = imfilter(I, h)",
    description: "Apply spatial filter kernel h to image I via convolution.",
    parameters: [
      { name: "I", type: "matrix", description: "Input image" },
      { name: "h", type: "matrix", description: "Filter kernel" },
    ],
    returns: "Filtered image matrix",
    examples: [
      { code: "imfilter(I, ones(3)/9)", description: "3×3 averaging filter" },
    ],
    seeAlso: ["fspecial", "edge", "conv2"],
  },
  edge: {
    name: "edge",
    category: "Image Processing",
    synopsis: "BW = edge(I)\nBW = edge(I, 'sobel')",
    description:
      "Detect edges in grayscale image using Sobel operator. Returns binary edge map.",
    parameters: [
      { name: "I", type: "matrix", description: "Grayscale image" },
      {
        name: "method",
        type: "string",
        description: "Edge detection method",
        optional: true,
        default: "'sobel'",
      },
    ],
    returns: "Binary edge matrix (0/1)",
    seeAlso: ["imfilter", "fspecial", "bwlabel"],
  },
  bwlabel: {
    name: "bwlabel",
    category: "Image Processing",
    synopsis: "L = bwlabel(BW)",
    description:
      "Label connected components in binary image using 4-connectivity BFS.",
    parameters: [{ name: "BW", type: "matrix", description: "Binary image" }],
    returns: "Label matrix where each connected component has a unique integer",
    seeAlso: ["regionprops", "edge"],
  },
  regionprops: {
    name: "regionprops",
    category: "Image Processing",
    synopsis: "regionprops(BW)",
    description:
      "Measure properties of labeled regions. Reports Area and Centroid for each region.",
    parameters: [
      { name: "BW", type: "matrix", description: "Label matrix from bwlabel" },
    ],
    returns: "Region property summary string",
    seeAlso: ["bwlabel", "edge"],
  },
  fspecial: {
    name: "fspecial",
    category: "Image Processing",
    synopsis: "h = fspecial(type)\nh = fspecial(type, size)",
    description:
      "Create predefined 2-D filter kernels: 'average', 'gaussian', 'sobel', 'prewitt', 'laplacian'.",
    parameters: [
      { name: "type", type: "string", description: "Filter type" },
      {
        name: "size",
        type: "number",
        description: "Kernel size",
        optional: true,
      },
      {
        name: "sigma",
        type: "number",
        description: "Gaussian sigma",
        optional: true,
      },
    ],
    returns: "Filter kernel matrix",
    examples: [
      {
        code: "fspecial('gaussian', 5, 1)",
        description: "5×5 Gaussian kernel",
      },
    ],
    seeAlso: ["imfilter", "edge"],
  },

  // ============================================================
  // SYMBOLIC MATH
  // ============================================================
  syms: {
    name: "syms",
    category: "Symbolic Math",
    synopsis: "syms x y z",
    description: "Declare symbolic variables for use in symbolic expressions.",
    parameters: [
      { name: "vars", type: "string", description: "Variable names" },
    ],
    returns: "Symbolic variable declaration string",
    seeAlso: ["sym", "symbolicDiff", "symbolicInt", "solve"],
  },
  sym: {
    name: "sym",
    category: "Symbolic Math",
    synopsis: "sym(expr)",
    description: "Create a symbolic expression from a string.",
    parameters: [
      { name: "expr", type: "string", description: "Expression string" },
    ],
    returns: "Symbolic expression",
    seeAlso: ["syms", "symbolicDiff"],
  },
  symbolicDiff: {
    name: "symbolicDiff",
    category: "Symbolic Math",
    synopsis: "symbolicDiff(expr, var)",
    description:
      "Symbolic differentiation of polynomial expressions. Supports terms like ax^n, ax, and constants.",
    parameters: [
      {
        name: "expr",
        type: "string",
        description: "Expression to differentiate",
      },
      {
        name: "var",
        type: "string",
        description: "Variable to differentiate with respect to",
        optional: true,
        default: "'x'",
      },
    ],
    returns: "Differentiated expression string",
    examples: [
      { code: "symbolicDiff('x^3+2*x^2+x+5', 'x')", result: '"3*x^2+4*x+1"' },
      { code: "symbolicDiff('3*x^2', 'x')", result: '"6*x"' },
    ],
    seeAlso: ["symbolicInt", "solve", "subs"],
  },
  symbolicInt: {
    name: "symbolicInt",
    category: "Symbolic Math",
    synopsis: "symbolicInt(expr, var)",
    description: "Symbolic integration of polynomial expressions.",
    parameters: [
      { name: "expr", type: "string", description: "Expression to integrate" },
      {
        name: "var",
        type: "string",
        description: "Variable of integration",
        optional: true,
        default: "'x'",
      },
    ],
    returns: "Integrated expression string",
    examples: [{ code: "symbolicInt('2*x', 'x')", result: '"x^2"' }],
    seeAlso: ["symbolicDiff", "solve"],
  },
  solve: {
    name: "solve",
    category: "Symbolic Math",
    synopsis: "solve(expr)",
    description:
      "Solve algebraic equations. Supports linear (ax+b=0) and quadratic (ax^2+bx+c=0) patterns.",
    parameters: [
      {
        name: "expr",
        type: "string",
        description: "Equation expression (=0 implied)",
      },
    ],
    returns: "Solution(s) as array",
    examples: [{ code: "solve('x^2-4')", result: "[2, -2]" }],
    seeAlso: ["fzero", "symbolicDiff", "subs"],
  },
  subs: {
    name: "subs",
    category: "Symbolic Math",
    synopsis: "subs(expr, var, val)",
    description:
      "Substitute a value for a variable in a symbolic expression and evaluate.",
    parameters: [
      { name: "expr", type: "string", description: "Expression" },
      { name: "var", type: "string", description: "Variable name" },
      { name: "val", type: "number", description: "Value to substitute" },
    ],
    returns: "Evaluated numeric result",
    examples: [{ code: "subs('x^2+1', 'x', 3)", result: "10" }],
    seeAlso: ["solve", "symbolicDiff"],
  },
  simplify: {
    name: "simplify",
    category: "Symbolic Math",
    synopsis: "simplify(expr)",
    description:
      "Simplify symbolic expression. Recognizes patterns like sin(x)^2+cos(x)^2=1.",
    parameters: [
      { name: "expr", type: "string", description: "Expression to simplify" },
    ],
    returns: "Simplified expression string",
    examples: [{ code: "simplify('sin(x)^2+cos(x)^2')", result: '"1"' }],
    seeAlso: ["syms", "subs"],
  },

  // ============================================================
  // DEEP LEARNING
  // ============================================================
  imageInputLayer: {
    name: "imageInputLayer",
    category: "Deep Learning",
    synopsis: "imageInputLayer(inputSize)",
    description: "Create an image input layer for a neural network.",
    parameters: [
      {
        name: "inputSize",
        type: "array",
        description: "Input image size [H W C]",
      },
    ],
    returns: "Layer specification string",
    seeAlso: ["fullyConnectedLayer", "convolution2dLayer"],
  },
  fullyConnectedLayer: {
    name: "fullyConnectedLayer",
    category: "Deep Learning",
    synopsis: "fullyConnectedLayer(outputSize)",
    description: "Create a fully connected (dense) layer.",
    parameters: [
      {
        name: "outputSize",
        type: "number",
        description: "Number of output neurons",
      },
    ],
    returns: "Layer specification string",
    seeAlso: ["imageInputLayer", "reluLayer", "softmaxLayer"],
  },
  reluLayer: {
    name: "reluLayer",
    category: "Deep Learning",
    synopsis: "reluLayer",
    description: "Create a ReLU activation layer.",
    returns: "Layer specification string",
    seeAlso: ["fullyConnectedLayer", "softmaxLayer"],
  },
  softmaxLayer: {
    name: "softmaxLayer",
    category: "Deep Learning",
    synopsis: "softmaxLayer",
    description:
      "Create a softmax activation layer for classification outputs.",
    returns: "Layer specification string",
    seeAlso: ["classificationLayer", "fullyConnectedLayer"],
  },
  classificationLayer: {
    name: "classificationLayer",
    category: "Deep Learning",
    synopsis: "classificationLayer",
    description:
      "Create a classification output layer with cross-entropy loss.",
    returns: "Layer specification string",
    seeAlso: ["softmaxLayer", "trainingOptions"],
  },
  convolution2dLayer: {
    name: "convolution2dLayer",
    category: "Deep Learning",
    synopsis: "convolution2dLayer(filterSize, numFilters)",
    description: "Create a 2-D convolution layer.",
    parameters: [
      {
        name: "filterSize",
        type: "number",
        description: "Size of convolution filters",
      },
      { name: "numFilters", type: "number", description: "Number of filters" },
    ],
    returns: "Layer specification string",
    seeAlso: ["maxPooling2dLayer", "imageInputLayer"],
  },
  maxPooling2dLayer: {
    name: "maxPooling2dLayer",
    category: "Deep Learning",
    synopsis: "maxPooling2dLayer(poolSize)",
    description: "Create a max pooling layer.",
    parameters: [
      { name: "poolSize", type: "number", description: "Pooling region size" },
    ],
    returns: "Layer specification string",
    seeAlso: ["convolution2dLayer"],
  },
  trainingOptions: {
    name: "trainingOptions",
    category: "Deep Learning",
    synopsis: "trainingOptions(solver)",
    description:
      "Create training options for network training. Supported solvers: 'sgdm', 'adam', 'rmsprop'.",
    parameters: [
      { name: "solver", type: "string", description: "Optimizer name" },
    ],
    returns: "Training options specification string",
    seeAlso: ["trainnet", "dlnetwork"],
  },
  trainnet: {
    name: "trainnet",
    category: "Deep Learning",
    synopsis: "trainnet(data, layers, loss, options)",
    description:
      "Train a neural network (stub — requires GPU/full MATLAB for actual training).",
    returns: "Status message",
    seeAlso: ["trainingOptions", "dlnetwork", "predict"],
  },
  dlnetwork: {
    name: "dlnetwork",
    category: "Deep Learning",
    synopsis: "dlnetwork(layers)",
    description: "Create a deep learning network object (stub).",
    parameters: [
      { name: "layers", type: "array", description: "Layer specifications" },
    ],
    returns: "Network specification string",
    seeAlso: ["trainnet", "predict"],
  },
  predict: {
    name: "predict",
    category: "Deep Learning",
    synopsis: "predict(net, X)",
    description:
      "Make predictions using a trained network (stub — requires trained model).",
    parameters: [
      { name: "net", type: "network", description: "Trained network" },
      { name: "X", type: "array | matrix", description: "Input data" },
    ],
    returns: "Prediction result",
    seeAlso: ["classify", "trainnet"],
  },
  classify: {
    name: "classify",
    category: "Deep Learning",
    synopsis: "classify(net, X)",
    description:
      "Classify data using a trained network (stub — requires trained model).",
    parameters: [
      { name: "net", type: "network", description: "Trained network" },
      { name: "X", type: "array | matrix", description: "Input data" },
    ],
    returns: "Classification labels",
    seeAlso: ["predict", "trainnet"],
  },
  relu: {
    name: "relu",
    category: "Deep Learning",
    synopsis: "relu(x)",
    description: "Element-wise Rectified Linear Unit: max(0, x).",
    parameters: [{ name: "x", type: "number | array", description: "Input" }],
    returns: "ReLU output",
    examples: [{ code: "relu([-2 -1 0 1 2])", result: "[0 0 0 1 2]" }],
    seeAlso: ["sigmoid", "softmax"],
  },
  sigmoid: {
    name: "sigmoid",
    category: "Deep Learning",
    synopsis: "sigmoid(x)",
    description: "Element-wise sigmoid activation: 1/(1+exp(-x)).",
    parameters: [{ name: "x", type: "number | array", description: "Input" }],
    returns: "Sigmoid output (0 to 1)",
    examples: [{ code: "sigmoid(0)", result: "0.5" }],
    seeAlso: ["relu", "softmax"],
  },
  softmax: {
    name: "softmax",
    category: "Deep Learning",
    synopsis: "softmax(x)",
    description:
      "Softmax function: converts logits to probability distribution.",
    parameters: [{ name: "x", type: "array", description: "Input logits" }],
    returns: "Probability vector summing to 1",
    seeAlso: ["sigmoid", "crossentropy"],
  },
  crossentropy: {
    name: "crossentropy",
    category: "Deep Learning",
    synopsis: "crossentropy(predictions, targets)",
    description: "Compute cross-entropy loss between predictions and targets.",
    parameters: [
      {
        name: "predictions",
        type: "array",
        description: "Predicted probabilities",
      },
      {
        name: "targets",
        type: "array",
        description: "Target labels (one-hot)",
      },
    ],
    returns: "Average cross-entropy loss",
    seeAlso: ["softmax"],
  },

  // ============================================================
  // ADDITIONAL ML
  // ============================================================
  fitcsvm: {
    name: "fitcsvm",
    category: "Statistics",
    synopsis: "fitcsvm(X, y)",
    description:
      "Train a linear SVM classifier using a perceptron-based approach.",
    parameters: [
      {
        name: "X",
        type: "matrix",
        description: "Training data (rows=samples, cols=features)",
      },
      { name: "y", type: "array", description: "Class labels" },
    ],
    returns: "SVM model description with weights and bias",
    seeAlso: ["fitlm", "kmeans", "pca"],
  },

  // ============================================================
  // UTILITY FUNCTIONS
  // ============================================================
  accumarray: {
    name: "accumarray",
    category: "Array Operations",
    synopsis: "accumarray(subs, val)",
    description: "Accumulate values by subscript indices (sum).",
    parameters: [
      { name: "subs", type: "array", description: "Subscript indices" },
      { name: "val", type: "array", description: "Values to accumulate" },
    ],
    returns: "Accumulated array",
    seeAlso: ["sum"],
  },
  cellfun: {
    name: "cellfun",
    category: "Array Operations",
    synopsis: "cellfun(func, C)",
    description: "Apply a function to each element of a cell array.",
    parameters: [
      {
        name: "func",
        type: "function_handle",
        description: "Function to apply",
      },
      { name: "C", type: "cell", description: "Cell array" },
    ],
    returns: "Array of results",
    seeAlso: ["arrayfun"],
  },
  arrayfun: {
    name: "arrayfun",
    category: "Array Operations",
    synopsis: "arrayfun(func, A)",
    description: "Apply a function to each element of an array.",
    parameters: [
      {
        name: "func",
        type: "function_handle",
        description: "Function to apply",
      },
      { name: "A", type: "array", description: "Input array" },
    ],
    returns: "Array of results",
    examples: [{ code: "arrayfun(@(x) x^2, [1 2 3])", result: "[1 4 9]" }],
    seeAlso: ["cellfun"],
  },
  fieldnames: {
    name: "fieldnames",
    category: "Data Containers",
    synopsis: "fieldnames(s)",
    description: "Return field names of a struct as a cell array.",
    parameters: [{ name: "s", type: "struct", description: "Input struct" }],
    returns: "Array of field name strings",
    seeAlso: ["struct", "isfield", "rmfield"],
  },
  isfield: {
    name: "isfield",
    category: "Data Containers",
    synopsis: "isfield(s, field)",
    description: "Check if a field exists in a struct.",
    parameters: [
      { name: "s", type: "struct", description: "Input struct" },
      { name: "field", type: "string", description: "Field name to check" },
    ],
    returns: "1 if field exists, 0 otherwise",
    seeAlso: ["fieldnames", "struct"],
  },
  sparse: {
    name: "sparse",
    category: "Matrix Operations",
    synopsis: "sparse(A)",
    description:
      "Convert to sparse representation (returns dense in this implementation).",
    parameters: [{ name: "A", type: "matrix", description: "Input matrix" }],
    returns: "Matrix (dense representation)",
    seeAlso: ["full", "nnz", "spy"],
  },
  full: {
    name: "full",
    category: "Matrix Operations",
    synopsis: "full(A)",
    description:
      "Convert sparse matrix to full (identity operation in this implementation).",
    parameters: [{ name: "A", type: "matrix", description: "Input matrix" }],
    returns: "Full matrix",
    seeAlso: ["sparse", "nnz"],
  },
  spy: {
    name: "spy",
    category: "Matrix Operations",
    synopsis: "spy(A)",
    description:
      "Visualize sparsity pattern of a matrix as text (* for nonzero, . for zero).",
    parameters: [{ name: "A", type: "matrix", description: "Input matrix" }],
    returns: "Sparsity pattern string",
    seeAlso: ["sparse", "nnz"],
  },

  // Quaternion Algebra
  quaternion: {
    name: "quaternion",
    category: "Quaternion",
    synopsis: "quaternion(w, x, y, z)",
    description:
      "Create a quaternion with scalar part w and vector part [x, y, z]. Quaternions are used for 3D rotations and can be multiplied, conjugated, and normalized.",
    parameters: [
      { name: "w", type: "number", description: "Scalar (real) part" },
      { name: "x", type: "number", description: "First imaginary component" },
      { name: "y", type: "number", description: "Second imaginary component" },
      { name: "z", type: "number", description: "Third imaginary component" },
    ],
    returns: "Quaternion object {type_: 'quaternion', w, x, y, z}",
    examples: [
      { code: "quaternion(1, 0, 0, 0)", result: "Identity quaternion" },
      { code: "quaternion(0, 1, 0, 0)", result: "90° rotation about x-axis" },
    ],
    seeAlso: ["quatmultiply", "quatconj", "quatnorm", "quatrotate"],
  },
  quatmultiply: {
    name: "quatmultiply",
    category: "Quaternion",
    synopsis: "quatmultiply(q1, q2)",
    description:
      "Multiply two quaternions. Quaternion multiplication is non-commutative and represents composition of rotations.",
    parameters: [
      { name: "q1", type: "quaternion", description: "First quaternion" },
      { name: "q2", type: "quaternion", description: "Second quaternion" },
    ],
    returns: "Product quaternion",
    seeAlso: ["quaternion", "quatconj", "quatinv"],
  },
  quatconj: {
    name: "quatconj",
    category: "Quaternion",
    synopsis: "quatconj(q)",
    description:
      "Compute the conjugate of a quaternion. Negates the vector part while keeping the scalar part.",
    parameters: [
      { name: "q", type: "quaternion", description: "Input quaternion" },
    ],
    returns: "Conjugate quaternion",
    seeAlso: ["quaternion", "quatinv", "quatnorm"],
  },
  quatnorm: {
    name: "quatnorm",
    category: "Quaternion",
    synopsis: "quatnorm(q)",
    description:
      "Compute the norm (magnitude) of a quaternion: sqrt(w² + x² + y² + z²).",
    parameters: [
      { name: "q", type: "quaternion", description: "Input quaternion" },
    ],
    returns: "Scalar norm value",
    seeAlso: ["quaternion", "quatnormalize"],
  },
  quatrotate: {
    name: "quatrotate",
    category: "Quaternion",
    synopsis: "quatrotate(q, v)",
    description:
      "Rotate a 3D vector v by quaternion q using the formula: v' = q * v * q⁻¹.",
    parameters: [
      { name: "q", type: "quaternion", description: "Rotation quaternion" },
      { name: "v", type: "array", description: "3D vector [x, y, z]" },
    ],
    returns: "Rotated 3D vector",
    seeAlso: ["quaternion", "quatmultiply", "axang2quat"],
  },
  slerp: {
    name: "slerp",
    category: "Quaternion",
    synopsis: "slerp(q1, q2, t)",
    description:
      "Spherical linear interpolation between two quaternions. t=0 returns q1, t=1 returns q2.",
    parameters: [
      { name: "q1", type: "quaternion", description: "Start quaternion" },
      { name: "q2", type: "quaternion", description: "End quaternion" },
      {
        name: "t",
        type: "number",
        description: "Interpolation parameter [0, 1]",
      },
    ],
    returns: "Interpolated quaternion",
    seeAlso: ["quaternion", "quatmultiply"],
  },

  // Rational Numbers
  rat: {
    name: "rat",
    category: "Symbolic",
    synopsis: "rat(x, tol?)",
    description:
      "Find a rational approximation to a decimal number using continued fractions. Returns a rational object with numerator and denominator.",
    parameters: [
      { name: "x", type: "number", description: "Value to approximate" },
      { name: "tol", type: "number", description: "Tolerance (default 1e-6)" },
    ],
    returns: "Rational object {numerator, denominator, value}",
    examples: [
      { code: "rat(0.333333)", result: "{numerator: 1, denominator: 3}" },
      { code: "rat(3.14159, 0.001)", result: "22/7 approximation" },
    ],
    seeAlso: ["rats", "rational", "numer", "denom"],
  },
  rats: {
    name: "rats",
    category: "Symbolic",
    synopsis: "rats(x, strlen?)",
    description:
      "Return a string representation of the rational approximation to x.",
    parameters: [
      { name: "x", type: "number", description: "Value to approximate" },
      {
        name: "strlen",
        type: "number",
        description: "Max string length (default 13)",
      },
    ],
    returns: "String like '1/3' or '22/7'",
    seeAlso: ["rat", "rational"],
  },
  rational: {
    name: "rational",
    category: "Symbolic",
    synopsis: "rational(num, den?)",
    description:
      "Create a rational number directly from numerator and denominator. The fraction is automatically reduced.",
    parameters: [
      { name: "num", type: "number", description: "Numerator" },
      { name: "den", type: "number", description: "Denominator (default 1)" },
    ],
    returns: "Rational object",
    examples: [
      { code: "rational(2, 4)", result: "{numerator: 1, denominator: 2}" },
    ],
    seeAlso: ["rat", "numer", "denom"],
  },

  // Arbitrary Precision
  vpa: {
    name: "vpa",
    category: "Symbolic",
    synopsis: "vpa(x, digits?)",
    description:
      "Variable-precision arithmetic. Convert x to a high-precision representation with specified decimal digits.",
    parameters: [
      { name: "x", type: "number", description: "Value to convert" },
      {
        name: "digits",
        type: "number",
        description: "Precision in digits (default 32)",
      },
    ],
    returns: "High-precision value object",
    examples: [{ code: "vpa(pi, 50)", result: "Pi with 50 digits" }],
    seeAlso: ["digits", "sym"],
  },
  digits: {
    name: "digits",
    category: "Symbolic",
    synopsis: "digits(d?)",
    description:
      "Get or set the default number of digits for variable-precision arithmetic.",
    parameters: [
      { name: "d", type: "number", description: "New precision (optional)" },
    ],
    returns: "Current precision setting",
    seeAlso: ["vpa"],
  },

  // ============================================================
  // FILE I/O AND WORKSPACE MANAGEMENT
  // ============================================================

  save: {
    name: "save",
    category: "File I/O",
    synopsis: "save(filename, var1, var2, ...)",
    description:
      "Save workspace variables to a file on the server. If no variables are specified, saves all variables. Files are stored in your personal workspace storage and can be loaded in any session.",
    parameters: [
      {
        name: "filename",
        type: "string",
        description:
          "Name of the file to save (e.g., 'mydata.mat'). The .mat extension is added automatically if not specified.",
      },
      {
        name: "var1, var2, ...",
        type: "variables",
        description:
          "Names of variables to save. If omitted, saves all workspace variables.",
        optional: true,
      },
    ],
    returns: "Success message with saved variables",
    examples: [
      {
        code: "x = 1:10; y = sin(x); save('trig_data')",
        description: "Save x and y to 'trig_data.mat'",
      },
      {
        code: "save('results', 'x', 'y')",
        description: "Save only x and y to 'results.mat'",
      },
    ],
    seeAlso: ["load", "who", "whos", "clear"],
  },

  load: {
    name: "load",
    category: "File I/O",
    synopsis: "load(filename, var1, var2, ...)",
    description:
      "Load variables from a previously saved file into the current workspace. Variables are loaded into the current session's environment.",
    parameters: [
      {
        name: "filename",
        type: "string",
        description:
          "Name of the file to load. The .mat extension is added automatically if not specified.",
      },
      {
        name: "var1, var2, ...",
        type: "variables",
        description:
          "Names of specific variables to load. If omitted, loads all variables from the file.",
        optional: true,
      },
    ],
    returns: "Success message with loaded variables",
    examples: [
      {
        code: "load('trig_data')",
        description: "Load all variables from 'trig_data.mat'",
      },
      {
        code: "load('results', 'x')",
        description: "Load only the variable 'x' from 'results.mat'",
      },
    ],
    seeAlso: ["save", "who", "whos", "clear"],
  },

  csvread: {
    name: "csvread",
    category: "File I/O",
    synopsis: "csvread(filename, rowOffset?, colOffset?)",
    description:
      "Read numeric data from a CSV file stored in your workspace. Returns the data as a matrix.",
    parameters: [
      {
        name: "filename",
        type: "string",
        description: "Name of the CSV file to read",
      },
      {
        name: "rowOffset",
        type: "number",
        description: "Number of rows to skip at the beginning (default: 0)",
        optional: true,
        default: "0",
      },
      {
        name: "colOffset",
        type: "number",
        description: "Number of columns to skip at the beginning (default: 0)",
        optional: true,
        default: "0",
      },
    ],
    returns: "Matrix containing the CSV data",
    examples: [
      {
        code: "data = csvread('measurements.csv')",
        description: "Read entire CSV file into a matrix",
      },
      {
        code: "data = csvread('data.csv', 1, 0)",
        description: "Skip the header row",
      },
    ],
    seeAlso: ["csvwrite", "importdata", "load"],
  },

  csvwrite: {
    name: "csvwrite",
    category: "File I/O",
    synopsis: "csvwrite(filename, data)",
    description: "Write matrix data to a CSV file in your workspace storage.",
    parameters: [
      {
        name: "filename",
        type: "string",
        description: "Name of the CSV file to create",
      },
      {
        name: "data",
        type: "matrix",
        description: "Matrix data to write to the file",
      },
    ],
    returns: "Success indicator",
    examples: [
      {
        code: "x = [1 2 3; 4 5 6]; csvwrite('output.csv', x)",
        description: "Write a 2×3 matrix to CSV",
      },
    ],
    seeAlso: ["csvread", "save"],
  },

  who: {
    name: "who",
    category: "Workspace",
    synopsis: "who",
    description:
      "List all variables currently defined in the workspace. Returns an array of variable names.",
    returns: "Array of variable names",
    examples: [
      {
        code: "x = 1; y = 2; who",
        result: "['x', 'y']",
        description: "List defined variables",
      },
    ],
    seeAlso: ["whos", "clear", "save", "load"],
  },

  whos: {
    name: "whos",
    category: "Workspace",
    synopsis: "whos",
    description:
      "Display detailed information about workspace variables including name, size, bytes, and class.",
    returns:
      "Array of variable information objects with name, size, bytes, and class",
    examples: [
      {
        code: "x = 1:10; A = magic(3); whos",
        description: "Show detailed info for all variables",
      },
    ],
    seeAlso: ["who", "clear", "save"],
  },

  clear: {
    name: "clear",
    category: "Workspace",
    synopsis: "clear(var1?, var2?, ...)",
    description:
      "Clear variables from the workspace. Without arguments, clears all user-defined variables. With arguments, clears only the specified variables.",
    parameters: [
      {
        name: "var1, var2, ...",
        type: "strings",
        description:
          "Names of variables to clear. If omitted, clears all variables.",
        optional: true,
      },
    ],
    returns: "0 (success)",
    examples: [
      {
        code: "clear",
        description: "Clear all workspace variables",
      },
      {
        code: "clear('x', 'y')",
        description: "Clear only x and y",
      },
    ],
    seeAlso: ["who", "whos", "save", "load"],
  },

  exist: {
    name: "exist",
    category: "Workspace",
    synopsis: "exist(name, type?)",
    description:
      "Check if a variable, function, or file exists. Returns different codes based on what exists.",
    parameters: [
      {
        name: "name",
        type: "string",
        description: "Name to check for existence",
      },
      {
        name: "type",
        type: "string",
        description:
          "Type to check: 'var' (variable), 'builtin' (function), or 'file'",
        optional: true,
      },
    ],
    returns: "0 = doesn't exist, 1 = variable, 2 = file, 5 = builtin function",
    examples: [
      {
        code: "x = 5; exist('x')",
        result: "1",
        description: "Variable exists",
      },
      {
        code: "exist('sin', 'builtin')",
        result: "5",
        description: "Built-in function exists",
      },
    ],
    seeAlso: ["who", "whos"],
  },

  dir: {
    name: "dir",
    category: "File I/O",
    synopsis: "dir(pattern?)",
    description:
      "List files in your workspace storage. Returns file information including name, size, and date.",
    parameters: [
      {
        name: "pattern",
        type: "string",
        description:
          "Optional wildcard pattern to filter files (e.g., '*.mat')",
        optional: true,
        default: "'*'",
      },
    ],
    returns: "Array of file info objects with name, isdir, bytes, and date",
    examples: [
      {
        code: "dir",
        description: "List all files",
      },
      {
        code: "dir('*.csv')",
        description: "List only CSV files",
      },
    ],
    seeAlso: ["save", "load", "exist"],
  },

  fileread: {
    name: "fileread",
    category: "File I/O",
    synopsis: "fileread(filename)",
    description: "Read the entire contents of a text file as a string.",
    parameters: [
      {
        name: "filename",
        type: "string",
        description: "Name of the text file to read",
      },
    ],
    returns: "File contents as a string",
    examples: [
      {
        code: "text = fileread('notes.txt')",
        description: "Read text file contents",
      },
    ],
    seeAlso: ["csvread", "importdata", "load"],
  },

  importdata: {
    name: "importdata",
    category: "File I/O",
    synopsis: "importdata(filename)",
    description:
      "Import data from a file, automatically detecting the format. Supports CSV, JSON, and text files.",
    parameters: [
      {
        name: "filename",
        type: "string",
        description: "Name of the file to import",
      },
    ],
    returns: "Imported data (type depends on file content)",
    examples: [
      {
        code: "data = importdata('measurements.csv')",
        description: "Import CSV data",
      },
    ],
    seeAlso: ["csvread", "load", "fileread"],
  },
};

/**
 * Get all documentation entries
 */
export function getAllDocs(): Array<{
  name: string;
  type: "function" | "constant" | "command";
  category?: string;
}> {
  const docs: Array<{
    name: string;
    type: "function" | "constant" | "command";
    category?: string;
  }> = [];

  for (const [name, fn] of Object.entries(functions)) {
    docs.push({ name, type: "function", category: fn.category });
  }

  for (const name of Object.keys(constants)) {
    docs.push({ name, type: "constant" });
  }

  for (const name of Object.keys(shellCommands)) {
    docs.push({ name, type: "command" });
  }

  return docs;
}

/**
 * Search documentation by keyword
 */
export function searchDocs(
  keyword: string,
): Array<FunctionDoc | ConstantDoc | CommandDoc> {
  const results: Array<FunctionDoc | ConstantDoc | CommandDoc> = [];
  const lowerKeyword = keyword.toLowerCase();

  for (const fn of Object.values(functions)) {
    if (
      fn.name.toLowerCase().includes(lowerKeyword) ||
      fn.description.toLowerCase().includes(lowerKeyword) ||
      fn.category.toLowerCase().includes(lowerKeyword)
    ) {
      results.push(fn);
    }
  }

  for (const constant of Object.values(constants)) {
    if (
      constant.name.toLowerCase().includes(lowerKeyword) ||
      constant.description.toLowerCase().includes(lowerKeyword)
    ) {
      results.push(constant);
    }
  }

  for (const cmd of Object.values(shellCommands)) {
    if (
      cmd.name.toLowerCase().includes(lowerKeyword) ||
      cmd.description.toLowerCase().includes(lowerKeyword)
    ) {
      results.push(cmd);
    }
  }

  return results;
}

import { matlab } from "@quanta/calcscript";

/**
 * Get documentation for a specific topic
 */
export function getDoc(
  topic: string,
): FunctionDoc | ConstantDoc | CommandDoc | null {
  const lowerTopic = topic.toLowerCase();

  if (functions[lowerTopic]) return functions[lowerTopic];
  if (constants[lowerTopic]) return constants[lowerTopic];
  if (shellCommands[lowerTopic]) return shellCommands[lowerTopic];

  // Fallback: check toolbox documentation
  const tbDoc = matlab.getToolboxFunctionDoc(lowerTopic);
  if (tbDoc) {
    return {
      name: tbDoc.name,
      category: tbDoc.toolbox,
      synopsis: tbDoc.synopsis,
      description: tbDoc.description,
      parameters: tbDoc.parameters,
      returns: tbDoc.returns,
      examples: tbDoc.examples,
      seeAlso: tbDoc.seeAlso,
    } as FunctionDoc;
  }

  return null;
}

/**
 * Get autocomplete suggestions based on prefix
 */
export function getAutocompleteSuggestions(prefix: string): Array<{
  name: string;
  type: "function" | "constant" | "command";
  synopsis?: string;
}> {
  const lowerPrefix = prefix.toLowerCase();
  const suggestions: Array<{
    name: string;
    type: "function" | "constant" | "command";
    synopsis?: string;
  }> = [];

  for (const [name, fn] of Object.entries(functions)) {
    if (name.toLowerCase().startsWith(lowerPrefix)) {
      suggestions.push({ name, type: "function", synopsis: fn.synopsis });
    }
  }

  for (const [name, constant] of Object.entries(constants)) {
    if (name.toLowerCase().startsWith(lowerPrefix)) {
      suggestions.push({ name, type: "constant", synopsis: constant.value });
    }
  }

  for (const [name, cmd] of Object.entries(shellCommands)) {
    if (name.toLowerCase().startsWith(lowerPrefix)) {
      suggestions.push({ name, type: "command", synopsis: cmd.synopsis });
    }
  }

  return suggestions.sort((a, b) => a.name.localeCompare(b.name));
}
